/* =====================================================================
   Cave Larégnère — SOMMELIER NUMÉRIQUE · MOTEUR
   =====================================================================
   Tout le raisonnement, et rien d'autre : ni HTML, ni style. Le moteur
   ne connaît que trois choses — le catalogue réel, la bibliothèque de
   recettes, et le savoir écrit dans cl-sommelier-savoir.js.

   IL FONCTIONNE SEUL
   Aucune intelligence artificielle, aucun service extérieur, aucune clé
   d'API : des règles explicites, lisibles et corrigeables. Une fois en
   ligne, il continue de fonctionner sans intervention, et s'améliore de
   lui-même à mesure que les fiches produits et les recettes sont
   complétées dans Shopify.

   CE QU'IL S'INTERDIT (règle n°23 du cahier des charges)
   • Ne jamais proposer une référence absente du catalogue.
   • Ne jamais afficher un prix ou une disponibilité qui ne vient pas de
     Shopify à l'instant présent.
   • Ne jamais présenter un produit épuisé comme achetable : il est
     écarté, et remplacé par une alternative cohérente.
   • Ne jamais forcer une correspondance de recette douteuse : l'écart
     est annoncé (« recette similaire »).
   • Ne jamais présenter un accord comme une vérité : c'est une
     proposition, toujours remplaçable.
   ===================================================================== */
(function (w) {
  'use strict';

  var S = w.CLSommelierSavoir;
  /* Connaissances APPRISES des données de la maison (fichier engendré par
     le script d'entraînement). Facultatif : sans lui, le moteur tourne
     exactement comme avant, sur le seul savoir écrit à la main. */
  var A = w.CLSommelierAppris || null;
  var M = {};

  /* Les trois fichiers du sommelier sont servis séparément par Shopify,
     chacun avec son empreinte de cache. Un navigateur peut donc, en
     théorie, se retrouver avec un moteur récent et un savoir périmé —
     et alors plus rien ne s'accorde, sans la moindre erreur visible.
     On déclare donc une version commune, que l'interface vérifie. */
  M.version = '7.0.0';

  /* =====================================================================
     1. OUTILS DE TEXTE
     ===================================================================== */

  /* Une seule et même normalisation partout : sans accent, sans
     ponctuation, en minuscules. C'est ce qui permet à « Magret de canard
     aux cèpes » de rencontrer le mot-clé « cepe ». */
  function norm(s) {
    return (s == null ? '' : String(s))
      .toLowerCase()
      /* ⚠️ LES LIGATURES D'ABORD. « œ » et « æ » ne sont pas des lettres
         a-z : le nettoyage ci-dessous les remplaçait par une espace, et
         « bœuf » devenait « b uf », « œufs » devenait « ufs ». Aucun mot
         de la bibliothèque ne pouvait plus correspondre — le bœuf
         bourguignon, les œufs en meurette et le filet de bœuf Richelieu
         n'étaient tout simplement pas reconnus. */
      .replace(/œ/g, 'oe').replace(/æ/g, 'ae')
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[’'`]/g, ' ')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }
  M.norm = norm;

  /* Reconnaissance d'un mot-clé dans un texte déjà normalisé.
     Un mot court se cherche PAR MOT ENTIER : sans cela, « bar » se
     retrouve dans « barbecue » et « gin » dans « gingembre », et le
     moteur propose un blanc de la mer sur des grillades. Les libellés
     longs ou composés (« saumon fume », « coquille saint jacques »)
     restent cherchés en sous-chaîne, où le risque n'existe pas.
     Le pluriel est admis : « cepe » retrouve « cepes ». */
  var CACHE_MOT = {};
  /* ⚠️ ON COMPARE DES MOTS, JAMAIS DES MORCEAUX DE MOTS (v6.0).
     Au-dessus de cinq lettres, cette fonction se contentait d'un
     `indexOf` : le mot-clé était cherché n'importe où dans la phrase,
     y compris AU MILIEU d'un autre mot. Résultat sur une carte de
     restaurant :
       « sauce hollandaise »  contient  « landaise »   → salade du Sud-Ouest,
         qui n'admet QUE le rouge et le rosé : quatre rouges servis sur
         un turbot au beurre. La faute la plus grave trouvée sur ce
         chantier, et elle était invisible dans les tests par plat parce
         qu'aucun archétype n'écrit « hollandaise ».
     Désormais le mot-clé doit occuper un mot entier, avec ses
     terminaisons françaises usuelles : « grille » reconnaît « grillée »,
     « canard » reconnaît « canards », mais « landaise » ne reconnaît
     plus « hollandaise ». La même règle vaut pour les expressions en
     plusieurs mots. */
  function contientMot(texte, mot) {
    if (!texte || !mot) return false;
    var re = CACHE_MOT[mot];
    if (!re) {
      re = CACHE_MOT[mot] = new RegExp(
        '(^| )' + mot.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '(e|es|ee|ees|s|x)?( |$)');
    }
    return re.test(texte);
  }
  M.contientMot = contientMot;

  /* =====================================================================
     TOLÉRANCE AUX FAUTES DE FRAPPE
     ---------------------------------------------------------------------
     « magrai de canar », « roquefor », « chocolatt », « cote de beuf » :
     personne ne tape sans faute sur un téléphone, et un outil qui répond
     « nous n'avons rien compris » sur une lettre en trop n'est pas
     utilisable.

     On compare donc les mots saisis aux mots-clés de la bibliothèque
     avec une distance d'édition de Damerau-Levenshtein — elle compte les
     lettres ajoutées, manquantes, remplacées ET INTERVERTIES, qui sont
     l'erreur la plus fréquente au clavier (« huirtes » pour « huîtres »).

     La tolérance dépend de la longueur du mot-clé, pour ne jamais
     confondre deux mots courts qui existent tous les deux :
       moins de 5 lettres .... aucune tolérance (« vin » ≠ « van »)
       5 ou 6 lettres ........ une faute
       7 lettres et plus ..... deux fautes

     Et l'approximatif ne prime jamais sur l'exact : une correspondance
     approchée pèse moins lourd, et le plat reconnu est annoncé au client
     pour qu'il puisse démentir.
     ===================================================================== */

  function distance(a, b) {
    if (a === b) return 0;
    var la = a.length, lb = b.length;
    if (Math.abs(la - lb) > 2) return 9;
    var d = [], i, j;
    for (i = 0; i <= la; i++) { d[i] = [i]; }
    for (j = 0; j <= lb; j++) { d[0][j] = j; }
    for (i = 1; i <= la; i++) {
      var minLigne = 9;
      for (j = 1; j <= lb; j++) {
        var cout = a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1;
        var v = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cout);
        /* Interversion de deux lettres voisines : « huirtes » / « huitres ». */
        if (i > 1 && j > 1 &&
            a.charAt(i - 1) === b.charAt(j - 2) &&
            a.charAt(i - 2) === b.charAt(j - 1)) {
          v = Math.min(v, d[i - 2][j - 2] + 1);
        }
        d[i][j] = v;
        if (v < minLigne) minLigne = v;
      }
      /* Sortie anticipée : au-delà de deux fautes, inutile de continuer. */
      if (minLigne > 2) return 9;
    }
    return d[la][lb];
  }

  /* ⚠️ SEUILS RELEVÉS (v6.0). Ils étaient à 5 et 7 lettres, et deux mots
     français bien réels ne sont souvent séparés que d'une lettre :
       « bûche » / « biche » ...... une bûche de Noël servie comme du GIBIER
       « norma » / « korma » ...... des pâtes siciliennes lues comme un curry
       « tofu »  / « thon »
     La tolérance existe pour rattraper une FAUTE DE FRAPPE, jamais pour
     inventer un plat que le client n'a pas écrit. Le coût des deux
     erreurs n'est pas le même : un mot mal orthographié se retrouve par
     ses voisins dans la phrase, un mauvais accord part au client. On
     n'accepte donc plus la moindre approximation en dessous de sept
     lettres, où vivent presque tous les faux amis. */
  /* Le palier est à SIX lettres, et ce chiffre est mesuré, pas choisi :
     on a listé tous les couples de mots de plats séparés d'une seule
     lettre. À cinq lettres, les faux amis abondent (« bûche »/« biche »,
     « norma »/« korma »). À six, il n'en reste qu'UN dans toute la
     bibliothèque — « braise » et « fraise » —, et la barrière d'étape
     s'en charge : l'un est un plat, l'autre un dessert. En dessous de
     six lettres, aucune approximation. */
  function tolerance(mot) {
    if (mot.length < 6) return 0;
    if (mot.length <= 9) return 1;
    return 2;
  }

  /* Un mot saisi ressemble-t-il au mot-clé attendu ? */
  function ressemble(saisi, cle) {
    if (saisi === cle) return true;
    var tol = tolerance(cle);
    if (!tol) return false;
    return distance(saisi, cle) <= tol;
  }

  /* Version tolérante de `contientMot`, réservée à la LECTURE DU PLAT.
     Elle ne sert jamais à classer un produit : les catégories du
     catalogue sont des données propres, on n'y cherche pas de faute. */
  function contientMotFlou(motsSaisis, cle) {
    var parties = cle.split(' ').filter(Boolean);
    if (!parties.length) return false;
    /* Sur un libellé en plusieurs mots, on peut être plus indulgent sur
       chaque mot : il faut de toute façon que TOUS tombent juste. C'est
       ce qui permet de lire « foi gras » — « foie » seul serait trop
       court pour tolérer quoi que ce soit sans confondre « œuf » et
       « bœuf ». */
    var souple = parties.length > 1;
    return parties.every(function (partie) {
      return motsSaisis.some(function (m) {
        if (m === partie) return true;
        var tol = tolerance(partie);
        if (!tol && souple && partie.length >= 4) tol = 1;
        return tol ? distance(m, partie) <= tol : false;
      });
    });
  }
  M.ressemble = ressemble;

  /* Racine grossière : « fromages » doit rencontrer « fromage ». On ne
     fait pas de vraie lemmatisation — inutile ici et coûteux — on ôte
     seulement la marque du pluriel.
     ATTENTION : on ne coupe QUE le « s » ou le « x » final. Couper « es »
     transformait « frites » en « frit », et une côte de bœuf avec frites
     se voyait proposer une recette de poulet frit coréen. Un caractère
     de trop dans une expression régulière, et tout l'outil perd sa
     crédibilité. */
  function racine(mot) {
    if (mot.length <= 4) return mot;
    return mot.replace(/[sx]$/, '');
  }

  function mots(texte) {
    var t = norm(texte).split(' ').filter(Boolean);
    var out = [];
    for (var i = 0; i < t.length; i++) {
      if (t[i].length < 3) continue;
      if (MOTS_VIDES.indexOf(t[i]) > -1) continue;
      out.push(racine(t[i]));
    }
    return out;
  }
  var MOTS_VIDES = ['avec', 'sans', 'pour', 'des', 'les', 'une', 'aux', 'sur', 'dans', 'plat', 'maison', 'fait', 'petit', 'grand', 'notre', 'mon', 'nos'];

  function clamp(v, min, max) { return v < min ? min : (v > max ? max : v); }

  /* Lit une mesure écrite à la main par la cave : « 6 g/Litre »,
     « Environ 7 à 8 g/L », « Brut – environ 10 g/L », « 12 et 17 g/L ».
     On rend le premier nombre, ou la moyenne quand deux nombres
     encadrent une fourchette. Retirer les caractères non numériques d'un
     bloc collait les chiffres : « 7 à 8 » devenait 78. */
  function premierNombre(texte) {
    var t = String(texte == null ? '' : texte).replace(/,/g, '.');
    var trouves = t.match(/\d+(\.\d+)?/g);
    if (!trouves || !trouves.length) return NaN;
    var a = parseFloat(trouves[0]);
    if (trouves.length > 1) {
      var b = parseFloat(trouves[1]);
      /* Une vraie fourchette reste proche : « 7 à 8 », « 0 à 3 ». Deux
         nombres très éloignés ne décrivent pas un intervalle (« cuvée
         n°748 – 2 g/L »), on garde alors le premier. */
      if (!isNaN(b) && b >= a && b <= a * 3 + 6) return (a + b) / 2;
    }
    return a;
  }
  M.premierNombre = premierNombre;

  /* =====================================================================
     2. CHARGEMENT DES DONNÉES RÉELLES
     ---------------------------------------------------------------------
     Le catalogue est publié par templates/collection.sommelier-json.liquid
     et les recettes par templates/blog.index-json.liquid. Les pages sont
     demandées en parallèle, puis gardées en mémoire pour la visite :
     l'assistant n'appelle donc le réseau qu'une seule fois, même si le
     client refait dix simulations (règle n°24 — appels réseau limités).

     Le cache est daté et versionné. En cas d'échec réseau, on ne
     travaille PAS sur des données inventées : on remonte l'erreur, et
     l'interface propose de réessayer.
     ===================================================================== */

  var CACHE_VERSION = 'cl-som-1';
  var CACHE_DUREE = 20 * 60 * 1000; /* 20 minutes : assez court pour que
                                       stocks et prix restent honnêtes. */

  function lireCache(cle) {
    try {
      var brut = w.sessionStorage.getItem(CACHE_VERSION + ':' + cle);
      if (!brut) return null;
      var o = JSON.parse(brut);
      if (!o || (Date.now() - o.t) > CACHE_DUREE) return null;
      /* UNE LISTE VIDE N'EST PAS UN CACHE VALABLE.
         En JavaScript, `if ([])` est vrai : un catalogue vide mis en
         cache une seule fois était donc resservi pendant toute la
         visite, sans la moindre erreur — les recettes s'affichaient, les
         bouteilles jamais. C'est exactement le symptôme « ça ne trouve
         rien ». On refuse le cache vide et on relit la cave. */
      if (!Array.isArray(o.d) || !o.d.length) return null;
      return o.d;
    } catch (e) { return null; }
  }
  function ecrireCache(cle, donnees) {
    /* Et on n'écrit jamais un résultat vide : mieux vaut redemander à la
       boutique que graver une absence. */
    if (!Array.isArray(donnees) || !donnees.length) return;
    try {
      w.sessionStorage.setItem(CACHE_VERSION + ':' + cle, JSON.stringify({ t: Date.now(), d: donnees }));
    } catch (e) { /* quota plein ou navigation privée : sans gravité */ }
  }

  /* EN APERÇU DE THÈME, LA DONNÉE DOIT VENIR DU MÊME THÈME QUE LA PAGE.
     Une page prévisualisée est servie par un thème non publié, mais une
     requête lancée depuis cette page peut très bien retomber sur le
     thème PUBLIÉ — où le gabarit `sommelier-json` n'existe pas encore.
     Shopify répond alors la page de collection en HTML, avec un code 200
     parfaitement rassurant, et l'assistant se retrouve sans catalogue.
     On repasse donc explicitement l'identifiant d'aperçu présent dans
     l'adresse, en plus des cookies. */
  function memeTheme(url) {
    try {
      var apercu = new URLSearchParams(w.location.search).get('preview_theme_id');
      if (!apercu) return url;
      return url + (url.indexOf('?') > -1 ? '&' : '?') + 'preview_theme_id=' + encodeURIComponent(apercu);
    } catch (e) { return url; }
  }

  /* NE JAMAIS ENVOYER « Accept: application/json » ICI.
     Ces adresses ne sont pas une API : ce sont des pages de la boutique
     rendues par un gabarit Liquid (`?view=sommelier-json`). Si la
     requête réclame du JSON, Shopify court-circuite le gabarit et
     répond sa propre représentation de la collection — 633 octets, sans
     un seul produit. La page se retrouve alors sans catalogue, sans
     erreur réseau, et l'assistant ne trouve plus rien.
     C'était la cause du « ça ne trouve rien » : aucun test en ligne de
     commande n'envoyait cet en-tête, seul le navigateur le faisait. */
  function json(url) {
    return fetch(memeTheme(url), { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status + ' sur ' + url);
        return r.text();
      })
      .then(function (texte) {
        /* Une page HTML renvoyée à la place du JSON n'est pas une panne
           réseau : c'est que le gabarit de données manque sur le thème
           affiché. On le dit précisément plutôt que d'accuser la
           connexion du visiteur. */
        var t = String(texte).trim();
        if (t.charAt(0) !== '{') throw new Error('GABARIT_ABSENT');
        try { return JSON.parse(t); }
        catch (e) { throw new Error('GABARIT_ABSENT'); }
      });
  }

  /* Demande la page 1, y lit le nombre total de pages, puis va chercher
     les suivantes — TROIS À LA FOIS, pas huit.
     Huit pages de collection demandées d'un coup, ce sont huit rendus
     Liquid simultanés : la boutique répond alors 429 (« trop de
     requêtes ») et le catalogue arrive incomplet, ou pas du tout. Trois
     de front reste rapide et ne fait jamais tousser le site. */
  var FRONT = 3;

  function chargerPagine(base, cleListe, surProgres) {
    var sep = base.indexOf('?') > -1 ? '&' : '?';
    return json(base + sep + 'page=1').then(function (p1) {
      var tout = (p1[cleListe] || []).slice();
      var pages = p1.pages || 1;
      /* Une première page sans aucun élément signale un gabarit ou une
         collection qui ne répond pas ce qu'on attend : on le dit plutôt
         que de continuer les mains vides. */
      if (!tout.length && pages <= 1) throw new Error('CATALOGUE_VIDE');
      /* On rend compte de l'avancement : lire tout un catalogue prend
         quelques secondes, et un écran qui n'annonce rien pendant ce
         temps-là se lit comme une panne. */
      if (surProgres) surProgres(1, pages, tout.length);
      if (pages <= 1) return tout;

      var files = [];
      for (var i = 2; i <= pages; i++) files.push(i);
      var resultats = [];

      var faites = 1;
      function suivante() {
        var page = files.shift();
        if (page === undefined) return Promise.resolve();
        return json(base + sep + 'page=' + page).then(function (p) {
          resultats.push({ page: page, data: p });
          faites++;
          if (surProgres) {
            var lus = tout.length;
            resultats.forEach(function (r) { lus += (r.data[cleListe] || []).length; });
            surProgres(faites, pages, lus);
          }
          return suivante();
        });
      }

      var courants = [];
      for (var k = 0; k < Math.min(FRONT, files.length); k++) courants.push(suivante());

      return Promise.all(courants).then(function () {
        /* Remise dans l'ordre : la concurrence ne doit pas mélanger le
           catalogue. */
        resultats.sort(function (a, b) { return a.page - b.page; });
        resultats.forEach(function (r) { tout = tout.concat(r.data[cleListe] || []); });
        return tout;
      });
    });
  }

  M.chargerCatalogue = function (collectionHandle, surProgres) {
    var handle = collectionHandle || 'tous-nos-produits';
    var cache = lireCache('cat:' + handle);
    if (cache) return Promise.resolve(cache.map(profilProduit));
    return chargerPagine('/collections/' + handle + '?view=sommelier-json', 'produits', surProgres)
      .then(function (liste) {
        ecrireCache('cat:' + handle, liste);
        return liste.map(profilProduit);
      });
  };

  M.chargerRecettes = function (blogs) {
    var handles = blogs && blogs.length ? blogs : ['recettes-de-cuisine', 'recettes-de-cocktails'];
    return Promise.all(handles.map(function (h) {
      var cache = lireCache('rec:' + h);
      if (cache) return Promise.resolve(cache);
      /* Gabarit propre au sommelier : les fiches produits gardent leur
         `index-json` inchangé, et ne paient donc rien pour nous. */
      return chargerPagine('/blogs/' + h + '?view=sommelier-json', 'articles')
        .then(function (liste) {
          liste.forEach(function (a) { a.blog = h; });
          ecrireCache('rec:' + h, liste);
          return liste;
        })
        /* Une bibliothèque indisponible ne doit pas empêcher les
           accords de fonctionner : on continue sans elle. */
        .catch(function () { return []; });
    })).then(function (paquets) {
      var tout = [];
      paquets.forEach(function (p) { tout = tout.concat(p); });
      return tout.map(profilRecette);
    });
  };

  /* =====================================================================
     3. LECTURE D'UN PRODUIT — de la fiche Shopify au profil de dégustation
     ---------------------------------------------------------------------
     On ne devine rien à partir du seul titre (règle n°11) : la famille
     vient du métachamp « Catégorie », complété par le type de produit ;
     le style vient des étiquettes, qui sont saisies pour cela ; le
     format vient du métachamp « Volume de la bouteille ».
     ===================================================================== */

  /* Shopify rend l'unité tantôt en toutes lettres (« CENTILITERS »),
     tantôt abrégée (« cl »), selon la façon dont le champ est lu. On
     accepte les deux écritures plutôt que de dépendre de l'une d'elles. */
  function volumeEnCl(vol) {
    if (!vol || typeof vol.v !== 'number') return null;
    var u = String(vol.u || '').toUpperCase().replace(/S$/, '');
    if (u === 'ML' || u.indexOf('MILLILITER') > -1) return vol.v / 10;
    if (u === 'CL' || u.indexOf('CENTILITER') > -1) return vol.v;
    if (u === 'L' || u.indexOf('LITER') > -1) return vol.v * 100;
    return vol.v;
  }

  function trouverFormat(texte, volCl) {
    var t = norm(texte);
    for (var i = 0; i < S.formats.length; i++) {
      var f = S.formats[i];
      for (var j = 0; j < f.mots.length; j++) {
        if (contientMot(t, norm(f.mots[j]))) return f;
      }
    }
    if (volCl) {
      /* À défaut de mot, le volume réel fait foi. */
      var best = null, ecart = 1e9;
      S.formats.forEach(function (f) {
        var d = Math.abs(f.cl - volCl);
        if (d < ecart) { ecart = d; best = f; }
      });
      if (ecart <= 8) return best;
    }
    return null;
  }

  /* Format habituel d'une famille, utilisé quand la fiche ne dit rien
     ou dit quelque chose d'impossible. */
  var CONTENANCE_USUELLE = {
    bulles: 75, blanc: 75, rouge: 75, rose: 75,
    spiritueux: 70, biere: 33, sans_alcool: 75, autre: 75
  };

  function profilProduit(p) {
    var o = Object.create(p);
    var cat = norm(p.categorie || p.type || '');
    var typ = norm(p.type || '');
    var app = norm(p.appellation || '');
    var tags = norm(p.tags || '').split(' ').join(' ');
    var recherche = cat + ' ' + typ + ' ' + app;

    /* --- Famille : ce que la CAVE a écrit prime sur le reste ---
       Deux passes. La première ne lit que le métachamp « catégorie »,
       rempli à la main fiche par fiche : c'est la seule source qui ne
       se trompe pas. La seconde, seulement s'il est vide, retombe sur
       le type et l'appellation — utile mais bavarde, c'est elle qui
       faisait d'un cognac de « Fine Champagne » un champagne. */
    o.famille = null;
    for (var i = 0; i < S.familles.length && !o.famille; i++) {
      var f = S.familles[i];
      for (var j = 0; j < f.detecte.length; j++) {
        if (contientMot(cat, norm(f.detecte[j]))) { o.famille = f; break; }
      }
    }
    for (var i2 = 0; i2 < S.familles.length && !o.famille; i2++) {
      var f2 = S.familles[i2];
      for (var j2 = 0; j2 < f2.detecte.length; j2++) {
        if (contientMot(recherche, norm(f2.detecte[j2]))) { o.famille = f2; break; }
      }
    }
    /* Un produit non identifié (épicerie, accessoire, éthylotest) ne
       participe pas aux accords : il est marqué, pas supprimé — il peut
       resservir pour les recommandations complémentaires. */
    if (!o.famille) o.famille = { id: 'autre', nom: p.type || 'Autre', groupe: 'autre', alcool: true, puissance: 1, verre: 12.5 };

    /* --- Couleur : lue dans la catégorie écrite par la cave ---
       C'est le filtre que le client actionne. On ne le déduit pas des
       tags ni du titre : seule la catégorie de la fiche fait foi, sinon
       un « Château Rousset » deviendrait un rouge. Le groupe de famille
       ne sert que de repêchage quand la catégorie ne dit rien. */
    o.couleur = null;
    for (var c = 0; c < (S.couleurs || []).length && !o.couleur; c++) {
      var col = S.couleurs[c];
      for (var d = 0; d < col.detecte.length; d++) {
        if (contientMot(cat, norm(col.detecte[d]))) { o.couleur = col.id; break; }
      }
    }
    if (!o.couleur) {
      var parGroupeCouleur = (S.couleurs || []).filter(function (x) { return x.groupe === o.famille.groupe; })[0];
      if (parGroupeCouleur) o.couleur = parGroupeCouleur.id;
    }

    /* --- Style : ce que disent les étiquettes de la maison --- */
    var texteStyle = tags + ' ' + norm(p.note || '') + ' ' + app + ' ' + norm(p.titre || '');
    o.styles = {};
    Object.keys(S.styles).forEach(function (k) {
      var n = 0;
      S.styles[k].forEach(function (mot) { if (contientMot(texteStyle, norm(mot))) n++; });
      if (n) o.styles[k] = n;
    });

    /* Puissance affinée : un rouge léger et un rouge de garde ne se
       servent pas au même moment du repas. */
    o.puissance = o.famille.puissance;
    if (o.famille.groupe === 'rouge') {
      if (o.styles.rouge_puissant) o.puissance += 0.5;
      if (o.styles.rouge_leger) o.puissance -= 0.5;
    }
    if (o.famille.groupe === 'blanc') {
      if (o.styles.blanc_riche) o.puissance += 0.3;
      if (o.styles.blanc_vif) o.puissance -= 0.2;
    }

    /* LE RANG DE L'APPELLATION — ce que vaut la bouteille, pas ce
       qu'elle coûte. Lu dans l'appellation écrite par la cave, jamais
       déduit du prix. */
    o.rang = 1;
    var texteApp = app + ' ' + cat;
    for (var rg = 0; rg < (S.rangs_appellation || []).length && o.rang === 1; rg++) {
      var niveau = S.rangs_appellation[rg];
      for (var rm = 0; rm < niveau.mots.length; rm++) {
        if (contientMot(texteApp, norm(niveau.mots[rm]))) { o.rang = niveau.rang; break; }
      }
    }

    o.bio = S.mots_bio.some(function (m) { return contientMot(tags, norm(m)); });
    o.alcool = o.famille.alcool !== false;

    /* =================================================================
       PROFIL ORGANOLEPTIQUE (v6.0) — ce que le sommelier lit dans le
       verre. Rien n'est saisi à la main : tout se déduit de ce que la
       cave a déjà rempli sur la fiche. Une bouteille qui entrera demain
       au catalogue aura donc son profil sans qu'on touche à ce fichier.
       Ordre des sources, du socle au plus précis :
         1. la famille          (un rouge a du tanin, un champagne des bulles)
         2. les CÉPAGES         (c'est eux qui font la structure — 181 fiches sur 193)
         3. l'appellation       (Pomerol n'est pas Beaujolais)
         4. le degré et le taux de sucre, quand ils sont renseignés
         5. les mots d'élevage lus dans la note de dégustation
       ================================================================= */
    var socle = (S.profil_familles || {})[o.famille.id] ||
                (S.profil_familles || {})[o.famille.groupe] ||
                { sucre: 0.5, acidite: 2, tanin: 1, corps: 2, alcool: 2, boise: 0.5, bulle: 0 };
    var prof = { sucre: socle.sucre, acidite: socle.acidite, tanin: socle.tanin,
                 corps: socle.corps, alcool: socle.alcool, boise: socle.boise,
                 bulle: socle.bulle, aromatique: socle.aromatique == null ? 1.5 : socle.aromatique,
                 /* Deux axes propres aux bières, nuls partout ailleurs :
                    l'amertume du houblon et la torréfaction du malt. */
                 amertume: socle.amertume == null ? 0 : socle.amertume,
                 torrefaction: socle.torrefaction == null ? 0 : socle.torrefaction };

    /* --- 2. Les cépages. On fait la MOYENNE des cépages reconnus : un
           assemblage merlot-cabernet n'a ni le tanin de l'un ni celui de
           l'autre, il a celui du mélange. Les axes qu'aucun cépage ne
           renseigne gardent la valeur de la famille. --- */
    var texteCep = norm(p.cepages || '');
    if (texteCep) {
      var cumul = {}, compte = {};
      (S.cepages || []).forEach(function (c) {
        var vu = c.mots.some(function (m) { return contientMot(texteCep, norm(m)); });
        if (!vu) return;
        Object.keys(c.profil).forEach(function (axe) {
          cumul[axe] = (cumul[axe] || 0) + c.profil[axe];
          compte[axe] = (compte[axe] || 0) + 1;
        });
      });
      Object.keys(cumul).forEach(function (axe) {
        var moyenne = cumul[axe] / compte[axe];
        /* Le cépage pèse deux tiers, la famille un tiers : sur un
           liquoreux, le sémillon ne doit pas effacer le sucre. */
        prof[axe] = (prof[axe] == null) ? moyenne : (prof[axe] * 0.34 + moyenne * 0.66);
      });
      o.cepages_lus = Object.keys(compte).length > 0;
    }

    /* --- 3. Le sucre écrit noir sur blanc l'emporte sur toute
           déduction : « 45 g/l » ne se discute pas.
       ⚠️ La cave n'écrit pas toujours un nombre sec. Sur les 33
       effervescents, on lit « Environ 7 à 8 g/L », « 12 et 17 g/Litre »,
       « Brut Nature – 0 g/L », « 0 à 3 g/L ». L'ancienne lecture retirait
       tout ce qui n'était pas un chiffre et COLLAIT les nombres :
       « 7 à 8 » devenait 78 g/l, donc un liquoreux. Trois champagnes
       bruts étaient ainsi profilés comme des vins doux. On lit
       maintenant les nombres un par un, et une fourchette donne sa
       moyenne. --- */
    /* --- 3 bis. LE STYLE DE LA BOUTEILLE, quand sa famille en a un.
           Un Blanc de Blancs extra-brut et un demi-sec sont deux vins
           différents ; une NEIPA juteuse et une Imperial Stout à 12°
           aussi. Le style est écrit dans la CATÉGORIE de la fiche —
           « Champagne Blanc de Blancs », « Imperial Stout », « DDH
           NEIPA ». On le lit dans la catégorie ET dans le titre, car la
           cave met parfois l'un, parfois l'autre. --- */
    var signature = cat + ' ' + norm(p.titre || '');
    /* LE PREMIER STYLE RECONNU L'EMPORTE, comme pour les familles : les
       tables sont écrites du plus précis au plus général. Sans cette
       règle, « New England IPA (NEIPA) » était lu d'abord comme une
       NEIPA (amertume 1,8) PUIS comme une IPA (3,4), et la seconde
       lecture écrasait la première — la bière la plus juteuse du rayon
       ressortait aussi amère qu'une Double IPA. */
    function appliquerStyles(table, poidsStyle) {
      for (var st = 0; st < (table || []).length; st++) {
        var style = table[st];
        if (!style.mots.some(function (m) { return contientMot(signature, norm(m)); })) continue;
        Object.keys(style.profil).forEach(function (axe) {
          prof[axe] = (prof[axe] == null) ? style.profil[axe]
                                          : prof[axe] * (1 - poidsStyle) + style.profil[axe] * poidsStyle;
        });
        return style;
      }
      return null;
    }
    var gSucre = premierNombre(p.sucre);
    if (o.famille.groupe === 'bulles') {
      /* Le DOSAGE : chiffré sur la fiche si la cave l'a écrit, sinon
         déduit des mots « brut nature », « extra brut », « demi-sec ». */
      if (isNaN(gSucre)) {
        for (var dz = 0; dz < (S.dosages || []).length; dz++) {
          if (S.dosages[dz].mots.some(function (m) { return contientMot(signature, norm(m)); })) {
            gSucre = S.dosages[dz].g;
            o.dosage_deduit = true;
            break;
          }
        }
      }
      appliquerStyles(S.styles_effervescent, 0.7);
    }
    if (o.famille.id === 'biere') appliquerStyles(S.styles_biere, 0.85);

    /* Le sucre réellement mesuré l'emporte sur toute déduction. */
    if (!isNaN(gSucre) && gSucre >= 0) {
      prof.sucre = gSucre < 4 ? 0.1 : gSucre < 12 ? 0.8 : gSucre < 18 ? 1.5 :
                   gSucre < 45 ? 2.5 : gSucre < 100 ? 3.4 : 4;
      o.sucre_g = gSucre;
    }

    /* --- 4. Le degré donne la chaleur. Un vin à 15° n'a pas la même
           place à table qu'un vin à 11,5°. --- */
    var deg = premierNombre(p.degre);
    if (!isNaN(deg) && deg > 0 && deg <= 60) {
      if (o.famille.id === 'biere') {
        /* ⚠️ Une bière ne se lit pas sur l'échelle du vin. À 12°, une
           Imperial Stout est un monstre de puissance ; l'échelle du vin
           lui donnait 1,4 sur 4, et une lager à 4,5° tombait à zéro —
           donc aucune chaleur, donc proposable partout. La bière a son
           propre barème : 3° à 13°. */
        prof.alcool = clamp((deg - 3) / 2.5, 0, 4);
      } else if (deg >= 30) prof.alcool = 4;
      else if (deg >= 20) prof.alcool = 3.4;
      else prof.alcool = clamp((deg - 9) / 2.2, 0, 4);
    }

    /* --- 5. Le bois se lit, il ne se devine pas. --- */
    var texteElevage = norm([p.note, p.caviste, p.tags, p.titre].join(' '));
    var nBois = 0;
    (S.mots_boise || []).forEach(function (m) { if (contientMot(texteElevage, norm(m))) nBois++; });
    if (nBois) prof.boise = Math.max(prof.boise, Math.min(3, 1 + nBois * 0.6));

    /* Le style déjà lu dans les étiquettes reste un correcteur utile
       quand la fiche n'a ni cépage ni degré. */
    if (o.styles.rouge_puissant) { prof.tanin += 0.4; prof.corps += 0.3; }
    if (o.styles.rouge_leger)    { prof.tanin -= 0.5; prof.corps -= 0.3; }
    if (o.styles.blanc_riche)    { prof.corps += 0.4; prof.acidite -= 0.2; }
    if (o.styles.blanc_vif)      { prof.acidite += 0.3; prof.corps -= 0.2; }
    if (o.styles.biere_forte)    { prof.corps += 0.6; prof.alcool += 0.5; }

    Object.keys(prof).forEach(function (k) { prof[k] = clamp(prof[k], 0, 4); });

    /* Un vin doux MUTÉ et un vin doux BOTRYTISÉ ne vont pas sur les
       mêmes desserts : le chocolat veut le premier, la tarte le second. */
    prof.mute = (S.familles_mutees || []).indexOf(o.famille.id) > -1;
    prof.botrytis = (S.familles_botrytis || []).indexOf(o.famille.id) > -1;
    o.profil = prof;

    /* --- Format réel --- */
    o.volume_cl = volumeEnCl(p.volume);
    /* Garde-fou : une fiche mal saisie (« 70 ml » pour une bouteille de
       70 cl) donnerait une contenance de 7 cl, donc des dizaines de
       bouteilles à acheter. Aucune boisson ne se vend sous 15 cl : en
       dessous, on ignore la valeur et on retient le format habituel de
       la famille. Mieux vaut un repère juste qu'un chiffre absurde
       (règle n°23). */
    if (o.volume_cl != null && o.volume_cl < 15) {
      o.volume_suspect = o.volume_cl;
      o.volume_cl = null;
    }
    var fmt = trouverFormat((p.titre || '') + ' ' + (p.tags || ''), o.volume_cl);
    o.format = fmt || { id: 'std', nom: 'Bouteille', cl: o.volume_cl || CONTENANCE_USUELLE[o.famille.groupe] || 75 };
    o.contenance = o.volume_cl || o.format.cl || CONTENANCE_USUELLE[o.famille.groupe] || 75;

    /* --- Formats alternatifs proposés par les variantes (magnum…) --- */
    o.formats_variantes = (p.variantes || []).map(function (v) {
      var f = trouverFormat(v.titre || '', null);
      return { id: v.id, titre: v.titre, prix: v.prix, dispo: v.dispo, format: f };
    });

    /* --- Texte servant aux rapprochements de plats --- */
    /* L'appellation seule, pour les accords de terroir : on ne veut pas
       qu'un « Château de Bergerac » situé ailleurs passe pour un vin de
       Bergerac au seul titre de son nom. */
    o.appellation_norm = app;

    o.texte_accords = norm(p.accords || '');
    /* Les étiquettes arrivent séparées par « | » et avec leurs accents :
       on en garde une version normalisée, seule comparable aux mots du
       plat, qui le sont eux aussi. */
    o.tags_norm = tags;
    o.texte_recherche = norm([p.titre, p.appellation, p.pays, p.cepages, p.accords, p.tags].join(' '));
    /* CE QUI IDENTIFIE LA BOUTEILLE, et rien d'autre. `texte_recherche`
       contient aussi les accords et les étiquettes : un punch au rhum
       dont la fiche parle d'« agricole » passait pour un rhum agricole.
       Pour rattacher un ingrédient de cocktail, on ne lit que le nom, la
       catégorie, l'appellation et les cépages. */
    /* Pas les cépages : celui d'un punch annonce « Rhum agricole à base
       de pur jus de canne », ce qui le faisait passer pour un rhum
       agricole. On s'en tient au nom, à la catégorie et à l'origine. */
    o.texte_bouteille = norm([p.titre, p.categorie, p.type, p.appellation].join(' '));

    o.prix_eur = (p.prix || 0) / 100;
    return o;
  }

  function profilRecette(a) {
    var o = Object.create(a);
    o.mots = mots([a.titre, a.tags, a.ingr, a.categorie, a.cuisine].join(' '));
    o.produits_liste = [];
    if (a.produits) {
      try {
        var v = typeof a.produits === 'string' ? JSON.parse(a.produits) : a.produits;
        if (Array.isArray(v)) o.produits_liste = v;
      } catch (e) { /* champ mal formé : on l'ignore plutôt que de planter */ }
    }
    o.est_cocktail = (a.blog || '').indexOf('cocktail') > -1;
    return o;
  }

  /* =====================================================================
     4. LECTURE D'UN PLAT — du langage naturel au profil gustatif
     ---------------------------------------------------------------------
     Trois lectures successives, de la plus sûre à la plus prudente :
       a) les archétypes de la bibliothèque (« magret » → canard) ;
       b) à défaut, les ingrédients isolés (« crème », « piment »…) ;
       c) à défaut, le profil moyen de l'étape du repas.
     On mémorise le degré de certitude : l'interface le dit au client
     plutôt que de faire semblant.
     ===================================================================== */

  M.analyserPlat = function (texte, etapeId, regles) {
    var t = norm(texte);
    var res = {
      texte: texte, etape: etapeId,
      axes: {}, archetypes: [], familles: {}, style: {},
      certitude: 0, raisons: {}, deconseille: {}, question: null
    };
    if (!t) return res;

    var bibliotheque = (regles && regles.plats) ? S.plats.concat(regles.plats) : S.plats;
    var motsSaisis = t.split(' ').filter(Boolean);

    /* --- a) Archétypes : d'abord à la lettre, puis à la ressemblance --- */
    bibliotheque.forEach(function (plat) {
      var touche = 0, motLong = 0, approche = false, exact = 0;
      plat.mots.forEach(function (mot) {
        var m = norm(mot);
        if (!m) return;
        if (contientMot(t, m)) { touche++; exact++; motLong = Math.max(motLong, m.length); return; }
        /* Rien à la lettre : on tolère la faute de frappe, mais ce
           rapprochement compte pour moitié — l'exact garde la main. */
        if (contientMotFlou(motsSaisis, m)) {
          touche += 0.5; approche = true;
          motLong = Math.max(motLong, m.length);
        }
      });
      if (!touche) return;
      /* SECONDE BARRIÈRE CONTRE LES FAUX AMIS : une lecture qui ne tient
         qu'à une ressemblance doit au moins tomber au bon MOMENT DU
         REPAS. « Bûche » ne peut pas devenir « biche » quand le client
         parle de son dessert. Une lecture exacte, elle, n'est jamais
         écartée : « salade » cité dans un plat reste une salade. */
      if (exact === 0 && etapeId && plat.etape && plat.etape !== etapeId) return;
      /* Un mot long et précis (« saumon fume ») pèse plus qu'un mot
         court et vague (« pate »). */
      var poids = touche * (motLong >= 8 ? 1.6 : motLong >= 6 ? 1.2 : 0.8);
      /* « Approché » ne vaut que si RIEN n'a été reconnu à la lettre dans
         ce plat. Sans cette réserve, « dorade au four » — pourtant lu
         exactement — s'annonçait « compris comme : poisson grillé »
         simplement parce que « daurade » avait aussi matché de justesse. */
      res.archetypes.push({ plat: plat, poids: poids, approche: approche && exact === 0, exact: exact });
    });
    /* L'EXACT CHASSE L'APPROXIMATIF.
       Dès qu'un plat est reconnu à la lettre, on écarte ceux qui ne
       tenaient qu'à une ressemblance : sans cela, « cotte de boeuf »
       faisait entrer le comté dans la lecture, et le plat devenait un
       plateau de fromages. La tolérance sert à rattraper une faute, pas
       à ajouter des plats que personne n'a écrits. */
    if (res.archetypes.some(function (a) { return a.exact > 0; })) {
      res.archetypes = res.archetypes.filter(function (a) { return a.exact > 0; });
    }

    /* UN PLAT PRÉCIS SUPPLANTE LE PLAT GÉNÉRIQUE QU'IL CONTIENT.
       « Salade périgourdine » contient le mot « salade », et les deux
       lectures se partageaient le plat à parts presque égales — d'où un
       blanc vif servi sur des gésiers confits. Le plat nommé
       explicitement dans `supplante` ne disparaît pas (il reste vrai
       qu'il y a des feuilles et de la vinaigrette), il cesse simplement
       de peser autant que le plat qui le précise. */
    var supplantes = {};
    res.archetypes.forEach(function (a) {
      if (a.exact > 0) (a.plat.supplante || []).forEach(function (id) { supplantes[id] = true; });
    });
    res.archetypes.forEach(function (a) {
      if (supplantes[a.plat.id]) a.poids *= 0.25;
    });

    /* LA GARNITURE NE COMMANDE PAS LE VIN.
       « Poulet rôti et pommes de terre » se lisait à 57 % comme un plat
       de légumes et à 43 % seulement comme une volaille : les pommes de
       terre choisissaient la bouteille. Un accompagnement — légumes,
       purée, gratin, frites — compte dans la lecture (il change le gras
       de l'assiette) mais il ne décide jamais à la place de la pièce
       principale. Dès qu'une vraie pièce est reconnue, l'accessoire
       tombe au tiers de son poids. */
    var aUnePiece = res.archetypes.some(function (a) { return a.exact > 0 && !a.plat.accessoire; });
    if (aUnePiece) {
      res.archetypes.forEach(function (a) { if (a.plat.accessoire) a.poids *= 0.33; });
    }

    res.archetypes.sort(function (a, b) { return b.poids - a.poids; });
    /* Au-delà de trois, on brasse du bruit : « barbecue avec entrecôte
       et saucisses » a besoin de deux ou trois lectures, pas de dix. */
    res.archetypes = res.archetypes.slice(0, 3);

    var poidsTotal = 0;
    res.archetypes.forEach(function (a) { poidsTotal += a.poids; });

    res.archetypes.forEach(function (a) {
      var part = a.poids / poidsTotal;
      /* La part de chaque lecture est conservée : elle sert ensuite à
         peser les signaux littéraux (« la fiche dit salade »), qui
         doivent suivre l'importance réelle du plat dans l'assiette. */
      a.part = part;
      Object.keys(a.plat.axes || {}).forEach(function (k) {
        res.axes[k] = (res.axes[k] || 0) + a.plat.axes[k] * part;
      });
      Object.keys(a.plat.style || {}).forEach(function (k) {
        res.style[k] = (res.style[k] || 0) + a.plat.style[k] * part;
      });
      Object.keys(a.plat.raisons || {}).forEach(function (k) {
        if (!res.raisons[k]) res.raisons[k] = a.plat.raisons[k];
      });
      /* Une contre-indication ne vaut que si l'archétype pèse vraiment
         dans la lecture du plat : « salade » cité en garniture d'une
         entrecôte ne doit pas interdire le rouge. */
      if (part >= 0.34) {
        Object.keys(a.plat.deconseille || {}).forEach(function (k) {
          if (!res.deconseille[k]) res.deconseille[k] = a.plat.deconseille[k];
        });
      }
    });

    /* LE TERROIR DU PLAT.
       Un plat régional appelle le vin de sa région : c'est le premier
       réflexe d'un caviste, et c'est ce qui sépare un bon accord d'un
       accord juste. Sans cela, un rosé de Provence pouvait passer
       devant un Bergerac sur une périgourdine — l'un est correct,
       l'autre est ce qu'on sert ici. Seuls les archétypes qui pèsent
       vraiment dans la lecture apportent leur terroir. */
    res.terroir = [];
    res.archetypes.forEach(function (a) {
      if ((a.poids / poidsTotal) < 0.34) return;
      (a.plat.terroir || []).forEach(function (t) {
        if (res.terroir.indexOf(t) < 0) res.terroir.push(t);
      });
    });

    /* COULEURS ADMISES — la porte fermée.
       Certains plats n'admettent pas toutes les couleurs, quoi qu'en
       dise le score : du blanc sur des gésiers confits reste une faute
       même le jour où la cave n'a plus qu'un très beau blanc. C'est la
       lecture DOMINANTE qui décide — la plus précise, celle qui a
       supplanté les autres —, jamais une lecture accessoire : « salade »
       cité en garniture ne doit pas fermer la porte du plat principal. */
    res.couleurs_admises = null;
    for (var ca = 0; ca < res.archetypes.length; ca++) {
      var pl = res.archetypes[ca].plat;
      if (pl.couleurs_admises && (res.archetypes[ca].poids / poidsTotal) >= 0.5) {
        res.couleurs_admises = pl.couleurs_admises.slice();
        break;
      }
    }

    /* UN ARCHÉTYPE PEUT LEVER LA MISE EN GARDE D'UN AUTRE.
       « Salade » déconseille le rouge à cause de la vinaigrette ; une
       périgourdine reste une salade, mais ses gésiers confits changent
       l'assiette au point que la mise en garde devient fausse. Le plat
       le plus précis a donc le dernier mot sur le plus général. */
    res.archetypes.forEach(function (a) {
      (a.plat.leve_deconseille || []).forEach(function (k) { delete res.deconseille[k]; });
    });

    /* --- Familles : moyenne pondérée, et non maximum ---
       Sur « comté et roquefort », la bouteille doit convenir à TOUTE
       l'assiette. Retenir le maximum reviendrait à ne juger que du
       fromage le plus accommodant, et à servir un rouge sur un bleu.
       Une famille absente d'un archétype y compte donc pour zéro :
       c'est ce qui fait redescendre l'accord qui ne vaut que pour la
       moitié du plat. */
    var toutesFamilles = {};
    res.archetypes.forEach(function (a) {
      Object.keys(a.plat.familles || {}).forEach(function (k) { toutesFamilles[k] = true; });
    });
    Object.keys(toutesFamilles).forEach(function (k) {
      var somme = 0;
      res.archetypes.forEach(function (a) {
        somme += (a.plat.familles[k] || 0) * (a.poids / poidsTotal);
      });
      res.familles[k] = somme;
    });

    if (res.archetypes.length) res.certitude = clamp(0.55 + 0.15 * res.archetypes.length, 0, 0.95);

    /* Une lecture obtenue par ressemblance se dit : le client doit
       pouvoir démentir « nous avons compris huîtres » d'un coup d'œil. */
    if (res.archetypes.length && res.archetypes[0].approche) {
      res.corrige = res.archetypes[0].plat.nom;
      res.certitude = Math.max(0.4, res.certitude - 0.15);
    }

    /* Le registre du plat = le plus exigeant des archétypes reconnus.
       « côte de bœuf avec frites » lit une belle pièce ET des frites :
       c'est la pièce qui décide du standing de la bouteille. */
    res.registre = 1;
    res.archetypes.forEach(function (a) {
      if ((a.plat.registre || 1) > res.registre) res.registre = a.plat.registre || 1;
    });

    /* --- a bis) CE QUE LA MAISON A ÉCRIT, APPRIS DE SES PROPRES DONNÉES
           326 fiches « Accords mets » et 1 090 couples plat/bouteille
           tirés des recettes. Cette table couvre 256 mots de plats, là où
           la bibliothèque écrite à la main en couvre 83 : c'est elle qui
           permet de répondre sur un plat que personne n'avait prévu.

           Elle COMPLÈTE, elle ne renverse pas : quand un archétype a
           reconnu le plat, l'apprentissage ne pèse qu'un tiers. Le savoir
           du caviste garde la main, les statistiques comblent les trous.
           --- */
    if (A && A.familles) {
      var poidsAppris = res.archetypes.length ? 0.35 : 0.9;
      var motsPlat = mots(texte);
      var vuAppris = false;
      motsPlat.forEach(function (m) {
        var table = A.familles[m];
        if (!table) return;
        vuAppris = true;
        Object.keys(table).forEach(function (f) {
          var note = table[f] * poidsAppris;
          if (note > (res.familles[f] || 0)) res.familles[f] = note;
        });
      });

      /* Les bouteilles que la cave a elle-même citées pour ce plat dans
         ses recettes. Ce n'est pas une déduction : c'est son choix. */
      res.produits_appris = {};
      motsPlat.forEach(function (m) {
        var liste = (A.produits && A.produits[m]) || [];
        liste.forEach(function (h, rang) {
          var force = Math.max(0.35, 0.9 - rang * 0.1);
          if (force > (res.produits_appris[h] || 0)) res.produits_appris[h] = force;
        });
      });

      if (vuAppris && !res.archetypes.length) res.certitude = Math.max(res.certitude, 0.5);
    }

    /* --- b) Ingrédients isolés, en complément ou à défaut --- */
    var vusIngredient = 0;
    S.ingredients.forEach(function (ing) {
      ing.mots.forEach(function (mot) {
        if (contientMot(t, norm(mot)) || contientMotFlou(motsSaisis, norm(mot))) {
          vusIngredient++;
          Object.keys(ing.axes).forEach(function (k) {
            res.axes[k] = Math.max(res.axes[k] || 0, ing.axes[k]);
          });
        }
      });
    });
    if (!res.archetypes.length && vusIngredient) res.certitude = 0.4;

    /* --- b bis) Ingrédients appris des recettes : dernier vrai signal
           avant le profil générique. « zarzuela » est inconnu, mais s'il
           contient des moules et du safran, la cave a déjà dit ce qu'elle
           sert avec. --- */
    if (A && A.ingredients && !res.archetypes.length) {
      mots(texte).forEach(function (m) {
        var table = A.ingredients[m];
        if (!table) return;
        vusIngredient++;
        Object.keys(table).forEach(function (f) {
          var note = table[f] * 0.7;
          if (note > (res.familles[f] || 0)) res.familles[f] = note;
        });
      });
    }

    /* --- c) PLAT NON RECONNU : ne rien inventer en silence ------------
       ⚠️ C'EST LA FAUTE QUI A SORTI UN CHARDONNAY SUR UN « BOURGUIGNON ».
       Le mot « bourguignon » seul n'était dans aucun archétype — seul
       « bœuf bourguignon » y figurait. Le plat tombait donc ici, et ce
       repli distribuait QUATRE familles à la fois : rouge 76, blanc sec
       62, blanc riche 58, rosé 45. Le blanc passait le plancher de
       qualité et raflait la case la moins chère. À l'écran : un
       chardonnay à 5,90 € en tête d'un bourguignon.

       Deux corrections, et la seconde compte plus que la première :
       1. le repli ne retient plus qu'UNE SEULE famille — la dominante de
          l'étape. Un conseil incertain doit au moins être cohérent : on
          ne panache pas du blanc et du rouge sur un plat qu'on n'a pas
          compris ;
       2. on lève le drapeau `inconnu`, pour que l'écran le DISE et
          invite à préciser, au lieu de faire passer une devinette pour
          un conseil de caviste.
       ------------------------------------------------------------------ */
    if (!res.archetypes.length && !vusIngredient) {
      res.certitude = 0.15;
      res.inconnu = true;
      var parDefaut = {
        apero:    { champagne: 80, effervescent: 78 },
        entree:   { blanc_sec: 80 },
        plat:     { rouge: 76 },
        fromage:  { blanc_riche: 78, rouge: 70 },
        dessert:  { moelleux: 82, liquoreux: 78 },
        digestif: { armagnac: 85, rhum: 82, cognac: 80, whisky: 78, calvados: 74 }
      };
      res.familles = parDefaut[etapeId] || parDefaut.plat;
    }

    /* --- Question de levée de doute, au plus une --- */
    for (var q = 0; q < S.questions.length && !res.question; q++) {
      var Q = S.questions[q];
      var declenche = Q.si.some(function (m) { return contientMot(t, norm(m)); });
      var deja = Q.sauf.some(function (m) { return contientMot(t, norm(m)); });
      if (declenche && !deja) res.question = Q;
    }

    /* CE PLAT APPELLE-T-IL UN VIN DOUX ?
       La question n'a pas de réponse dans les axes : le foie gras et le
       roquefort ne sont pas « sucrés », et pourtant ce sont les deux
       plats où l'on sert un liquoreux. C'est la bibliothèque qui le
       sait, en accordant une grosse note aux familles douces. On pose
       donc le drapeau ici, pour que les lois de structure ne pénalisent
       pas le sucre résiduel là où il est précisément attendu.
       Sans lui, la loi « pas de sucre sur un plat salé » faisait reculer
       le Rivesaltes 20 ans sur le foie gras — que Grégoire a validé — et
       aurait fini par écarter le Monbazillac du roquefort. */
    var appelDoux = 0;
    ['moelleux', 'liquoreux', 'vdn'].forEach(function (f) {
      if ((res.familles[f] || 0) > appelDoux) appelDoux = res.familles[f];
    });
    res.axes.veut_doux = appelDoux >= 35 ? 1 : 0;

    res.mots = mots(texte);
    return res;
  };

  /* =====================================================================
     4 bis. UN SEUL CHAMP, UN MENU ENTIER
     ---------------------------------------------------------------------
     Le client écrit son repas d'une traite — « huîtres, magret aux cèpes,
     roquefort, fondant au chocolat » — et l'assistant le découpe lui-même.
     Il n'y a donc AUCUN formulaire à remplir étape par étape : c'est la
     phrase qui décide du nombre de lignes.

     Le découpage se fait sur la ponctuation et sur quelques mots de
     liaison, jamais sur « et » : « huîtres et saumon fumé » est UN
     service, pas deux.

     Chaque morceau reçoit son moment du repas depuis la bibliothèque
     (« roquefort » → fromages). Un intitulé explicite écrit par le
     client (« dessert : tarte ») fait toujours autorité.
     ===================================================================== */

  var SEPARATEURS = /[,;\n\r\/•·]+|\s+puis\s+|\s+ensuite\s+|\s+suivi de\s+|\s+en dessert\s+|\s+pour finir\s+/i;

  /* Intitulés que le client peut écrire lui-même devant un plat. */
  var INTITULES = [
    { etape: 'apero',    mots: ['aperitif', 'apero', 'entree en matiere', 'amuse bouche'] },
    { etape: 'entree',   mots: ['entree', 'entrees'] },
    { etape: 'plat',     mots: ['plat', 'plat principal', 'resistance'] },
    { etape: 'fromage',  mots: ['fromage', 'fromages', 'plateau de fromages'] },
    { etape: 'dessert',  mots: ['dessert', 'desserts'] },
    { etape: 'digestif', mots: ['digestif', 'digestifs', 'cafe'] }
  ];

  /* =====================================================================
     LE TRADUCTEUR DE PLATS — l'IA comprend, le moteur décide
     ---------------------------------------------------------------------
     Appelé UNIQUEMENT quand la lecture locale n'a rien reconnu. Sur les
     640 plats du banc d'essai, cela n'arrive jamais : ce chemin est
     réservé au « truc de ma grand-mère avec du canard et des pruneaux »
     et aux plats régionaux qu'aucune liste ne contient.

     ⚠️ CE QUE L'IA NE FAIT PAS : elle ne choisit aucune bouteille et ne
     voit pas le catalogue. Elle rend des IDENTIFIANTS D'ARCHÉTYPES de
     notre propre bibliothèque, et rien d'autre. On rejoue ensuite
     `analyserPlat` sur le premier mot de ces archétypes — c'est-à-dire
     que la lecture repasse par EXACTEMENT le même code que d'habitude :
     mêmes familles, mêmes lois, mêmes portes fermées, même plancher de
     qualité. Aucun chemin de décision nouveau n'est ouvert à l'IA.

     Si le service est absent, lent ou en panne, `comprendrePlat` rend
     null et l'assistant garde sa lecture prudente. Le site ne dépend
     pas de lui.
     ===================================================================== */
  M.comprendrePlat = function (texte, etapeId, url) {
    if (!url || !texte) return Promise.resolve(null);
    var controle = null, minuterie = null;
    try { controle = new AbortController(); } catch (e) {}
    var attente = new Promise(function (resolve) {
      minuterie = setTimeout(function () {
        if (controle) { try { controle.abort(); } catch (e) {} }
        resolve(null);
      }, 6000);
    });
    var appel = fetch(url, {
      method: 'POST',
      /* Une vraie API, celle-là : l'en-tête Accept y est légitime.
         (À ne jamais faire sur les gabarits `?view=…-json`.) */
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ texte: String(texte).slice(0, 240), etape: etapeId || '' }),
      signal: controle ? controle.signal : undefined
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (lecture) {
        clearTimeout(minuterie);
        if (!lecture || !lecture.archetypes || !lecture.archetypes.length) return null;
        /* ON NE FAIT CONFIANCE QU'À NOTRE PROPRE VOCABULAIRE. Un
           identifiant que la bibliothèque ne connaît pas est jeté ici,
           même si le service l'a laissé passer. */
        var connus = lecture.archetypes.filter(function (id) {
          return S.plats.some(function (p) { return p.id === id; });
        });
        if (!connus.length) return null;
        lecture.archetypes = connus;
        return lecture;
      })
      .catch(function () { clearTimeout(minuterie); return null; });
    return Promise.race([appel, attente]);
  };

  /* Traduit la lecture de l'IA dans les mots du moteur, puis relance
     l'analyse normale. C'est volontairement indirect : on ne fabrique
     pas une analyse à la main, on redonne au moteur une phrase qu'il
     sait lire. */
  M.relireAvecLecture = function (lecture, etapeId, regles) {
    if (!lecture || !lecture.archetypes || !lecture.archetypes.length) return null;
    var phrase = lecture.archetypes.map(function (id) {
      var p = S.plats.filter(function (x) { return x.id === id; })[0];
      return p && p.mots && p.mots.length ? p.mots[0] : '';
    }).filter(Boolean).join(' et ');
    if (!phrase) return null;
    var etape = lecture.etape || etapeId;
    var res = M.analyserPlat(phrase, etape, regles);
    if (!res.archetypes.length) return null;
    /* On garde le nom que le client a écrit — l'écran doit lui parler de
       SON plat, pas de l'archétype auquel on l'a rattaché. */
    res.texte = lecture.plat_normalise || lecture.texte || res.texte;
    res.compris_par_ia = true;
    res.certitude = Math.min(0.8, lecture.confiance || 0.5);
    M.poserVinIdeal(res, lecture);
    return res;
  };

  /* LE PROFIL DU VIN VOULU, POSÉ SUR UNE LECTURE.
     C'est ici que le travail de sommelier de l'IA entre dans le moteur.
     Elle ne nomme aucune bouteille — elle décrit la structure du vin
     qu'elle servirait. Le moteur connaît déjà cette même structure pour
     chacune des 401 bouteilles de la cave : il n'a plus qu'à chercher la
     plus proche, parmi celles qui sont vraiment en stock et au vrai prix.
     Utilisable sur un plat DÉJÀ reconnu : on garde alors la lecture
     locale (familles, portes fermées, terroir) et on ajoute seulement la
     précision du sommelier. */
  M.poserVinIdeal = function (analyse, lecture) {
    if (!analyse || !lecture || !lecture.vin_ideal) return analyse;
    var vi = lecture.vin_ideal;
    if (!vi.couleurs || !vi.couleurs.length) return analyse;
    analyse.vin_ideal = vi;
    if (lecture.conseil) analyse.conseil_ia = lecture.conseil;
    return analyse;
  };

  /* À quelle distance une bouteille est-elle du vin demandé ? */
  function ecartAuVinIdeal(produit, vi) {
    var v = produit.profil;
    if (!v) return null;
    var fam = produit.famille;
    /* La couleur d'abord : c'est éliminatoire dans l'esprit du sommelier,
       mais on en fait ici un poids fort plutôt qu'un couperet — les
       portes fermées de la bibliothèque restent seules à interdire. */
    var etiquettes = [fam.id];
    if (fam.groupe === 'spiritueux') etiquettes.push('spiritueux');
    if (fam.id === 'blanc_sec' && produit.styles && produit.styles.blanc_riche) etiquettes.push('blanc_riche');
    var rangCouleur = -1;
    for (var i = 0; i < vi.couleurs.length; i++) {
      if (etiquettes.indexOf(vi.couleurs[i]) > -1) { rangCouleur = i; break; }
    }
    var ecart = 0;
    ['tanin', 'acidite', 'corps', 'sucre', 'bulle', 'boise'].forEach(function (axe) {
      if (vi[axe] == null) return;
      ecart += Math.abs((v[axe] || 0) - vi[axe]);
    });
    return { couleur: rangCouleur, ecart: ecart };
  }
  M.ecartAuVinIdeal = ecartAuVinIdeal;

  M.decouperMenu = function (texte, regles) {
    var brut = String(texte || '').split(SEPARATEURS);
    var lignes = [];

    brut.forEach(function (morceau) {
      var t = morceau.replace(/\s+/g, ' ').trim();
      if (t.length < 2) return;

      /* « Dessert : fondant au chocolat » — l'intitulé est retiré du
         texte, sinon il fausserait la lecture du plat. */
      var etapeForcee = null;
      var deuxPoints = t.indexOf(':');
      if (deuxPoints > 0 && deuxPoints <= 22) {
        var etiquette = norm(t.slice(0, deuxPoints));
        INTITULES.forEach(function (I) {
          if (etapeForcee) return;
          if (I.mots.some(function (m) { return etiquette === norm(m); })) {
            etapeForcee = I.etape;
            t = t.slice(deuxPoints + 1).trim();
          }
        });
      }
      if (!t) return;

      var analyse = M.analyserPlat(t, etapeForcee || 'plat', regles);
      var etape = etapeForcee;
      if (!etape && analyse.archetypes.length) etape = analyse.archetypes[0].plat.etape;
      if (!etape) etape = 'plat';

      /* La lecture est refaite avec le bon moment du repas : le profil
         par défaut d'un dessert n'est pas celui d'un plat. */
      if (!etapeForcee && etape !== 'plat') analyse = M.analyserPlat(t, etape, regles);
      analyse.etape = etape;

      lignes.push({ texte: t, etape: etape, analyse: analyse });
    });

    /* Remise dans l'ordre du repas, sans perdre l'ordre d'écriture entre
       deux plats d'un même moment. */
    return lignes.map(function (l, i) { return { l: l, i: i }; })
      .sort(function (a, b) {
        var oa = ordreEtape(a.l.etape), ob = ordreEtape(b.l.etape);
        return oa === ob ? a.i - b.i : oa - ob;
      })
      .map(function (x) { return x.l; });
  };

  function ordreEtape(id) {
    var e = S.etapes.filter(function (x) { return x.id === id; })[0];
    return e ? e.ordre : 99;
  }
  M.ordreEtape = ordreEtape;

  /* Applique la réponse du client à une question de levée de doute. */
  M.repondreQuestion = function (analyse, option) {
    Object.keys(option.axes || {}).forEach(function (k) {
      analyse.axes[k] = option.axes[k];
    });
    analyse.certitude = Math.min(0.9, analyse.certitude + 0.25);
    analyse.question = null;
    return analyse;
  };

  /* =====================================================================
     5. RECETTES — le savoir déjà écrit par la cave
     ---------------------------------------------------------------------
     Quand un plat correspond à une recette de la bibliothèque, son
     métachamp `recette.produits` donne les bouteilles choisies à la main
     par le caviste : c'est le signal le plus fiable dont dispose le
     moteur, et il passe devant toute règle générale.
     ===================================================================== */

  M.trouverRecettes = function (analyse, recettes, options) {
    var opt = options || {};
    var cible = analyse.mots || [];
    if (!cible.length || !recettes || !recettes.length) return [];
    var out = [];

    recettes.forEach(function (r) {
      if (opt.cocktail === true && !r.est_cocktail) return;
      if (opt.cocktail === false && r.est_cocktail) return;

      /* Le titre qui engage la maison est celui d'AVANT les deux-points.
         « Poularde à la Derby : la recette farcie au foie gras » est une
         recette de poularde, pas de foie gras — la sous-titre énumère
         des ingrédients, il ne dit pas le sujet. On ne rapproche donc
         que sur le vrai titre. */
      var titreNorm = norm(String(r.titre).split(':')[0]);
      var communs = 0;
      cible.forEach(function (m) { if (r.mots.indexOf(m) > -1) communs++; });
      if (!communs) return;

      /* Un mot retrouvé dans le TITRE vaut bien plus qu'un mot croisé
         au fond d'une liste d'ingrédients. */
      var dansTitre = 0;
      cible.forEach(function (m) { if (titreNorm.indexOf(m) > -1) dansTitre++; });

      /* LE MOT DU PLAT DOIT ÊTRE DANS LE TITRE DE LA RECETTE.
         Sans cette exigence, « huîtres » remontait une recette de
         nouilles thaïes — parce qu'elle contient de la SAUCE d'huître
         dans sa liste d'ingrédients. Un rapprochement pareil décrédibilise
         tout le reste : mieux vaut ne rien proposer. */
      if (!dansTitre) return;

      var score = communs / cible.length + dansTitre * 0.6;

      /* SEUIL DE RESSEMBLANCE. Un seul mot en commun ne suffit pas :
         « côte de bœuf » et « côtes de porc marinées » partagent « côte »
         et n'ont rien à voir. Le score mêle la part du plat retrouvée et
         la présence dans le titre ; en dessous de 1, on préfère ne rien
         proposer. Une recette hors sujet coûte plus cher en crédibilité
         qu'une absence de recette. */
      if (score < 1) return;

      /* DEUX MOTS, PAS UN. « Fondant au chocolat » et « bœuf bourguignon,
         fondant à la cuillère » partagent « fondant » — et rien d'autre.
         Sur un plat nommé en plusieurs mots, on exige donc au moins deux
         mots retrouvés ; sur un plat d'un seul mot (« raclette »), ce
         mot suffit, forcément. */
      if (communs < Math.min(2, cible.length)) return;

      out.push({ recette: r, score: score, communs: communs, dansTitre: dansTitre });
    });

    out.sort(function (a, b) { return b.score - a.score; });
    out = out.slice(0, 6);

    /* On distingue franchement ce qui est certain de ce qui ressemble
       (règle n°7 : ne pas créer de fausse correspondance exacte). */
    out.forEach(function (o) {
      /* « Notre recette » engage la maison : on ne l'écrit que si les
         mots du plat couvrent vraiment le titre. Sinon c'est « Recette
         proche », et c'est honnête. */
      o.exacte = o.dansTitre >= Math.max(1, cible.length) && o.score >= 1.4;
      o.libelle = o.exacte ? S.textes.recette_exacte : S.textes.recette_proche;
    });
    return out;
  };

  /* =====================================================================
     6. NOTATION D'UN PRODUIT POUR UN PLAT
     ===================================================================== */

  function scorer(produit, analyse, ctx) {
    /* --- Éliminations franches : un produit écarté ici ne sera jamais
           présenté comme achetable. --- */
    if (!produit.dispo) return null;                              /* règle n°23 */
    if (produit.famille.id === 'autre') return null;
    if (ctx.sansAlcool && produit.alcool) return null;
    if (!ctx.sansAlcool && ctx.parcours === 'alcool' && !produit.alcool && !ctx.autoriserSoft) return null;
    if (ctx.budgetMaxBouteille && produit.prix_eur > ctx.budgetMaxBouteille) return null;

    /* --- Couleur et catégorie demandées : un FILTRE, pas un penchant.
           Quand le client dit « du rouge », il ne demande pas qu'on
           remonte les rouges — il demande des rouges. On élimine donc
           franchement, et l'interface le dit quand la cave ne peut pas
           remplir la rangée. --- */
    if (ctx.couleurs && ctx.couleurs.length && ctx.couleurs.indexOf(produit.couleur) < 0) return null;
    if (ctx.categories && ctx.categories.length && !M.dansCategories(produit, ctx.categories)) return null;

    /* === UN VIN DOUX NE SE SERT PAS SUR UN PLAT SALÉ. =================
       Règle absolue, pas une pénalité de score. Un moelleux, un
       liquoreux ou un vin doux naturel n'a sa place que là où la cuisine
       l'appelle : le foie gras, le bleu, le sucré-salé, le piment, le
       dessert. Partout ailleurs c'est une faute, et une faute qu'aucun
       bon score par ailleurs ne doit pouvoir rattraper — ni un champ
       « Accords mets » bavard, ni une bouteille liée à la recette, ni
       une tranche de prix à remplir.
       Le juge, c'est la bibliothèque de plats elle-même : si le plat
       n'accorde pas au moins 35 points à cette famille, elle disparaît.
       Les desserts et les fromages gardent la porte ouverte, c'est leur
       terrain. ================================================== */
    var idFam = produit.famille ? produit.famille.id : '';
    if ((idFam === 'moelleux' || idFam === 'liquoreux' || idFam === 'vdn') &&
        analyse.etape !== 'dessert' && analyse.etape !== 'fromage' &&
        ((analyse.familles && analyse.familles[idFam]) || 0) < 35) return null;

    /* --- Les couleurs que le PLAT refuse. Un cran plus fort que la
           contre-indication, qui ne fait que pénaliser : ici la bouteille
           ne paraît pas du tout. Ne s'applique qu'au conseil du caviste —
           dès que le client a demandé une couleur lui-même, c'est lui qui
           décide, y compris pour se tromper. --- */
    if (!(ctx.couleurs && ctx.couleurs.length) &&
        analyse.couleurs_admises && analyse.couleurs_admises.length &&
        produit.couleur && analyse.couleurs_admises.indexOf(produit.couleur) < 0) return null;
    if (ctx.exclusions && ctx.exclusions.length) {
      for (var e = 0; e < ctx.exclusions.length; e++) {
        var ex = norm(ctx.exclusions[e]);
        if (ex && produit.texte_recherche.indexOf(ex) > -1) return null;
      }
    }

    var fam = produit.famille;
    var score = analyse.familles[fam.id];

    /* Les familles de vin sont notées par groupe (« rouge », « blanc »)
       autant que par identifiant : un plat qui aime les rouges aime tous
       les rouges, à charge pour le style de départager. */
    if (score == null) {
      var parGroupe = { rouge: 'rouge', blanc: 'blanc_sec', bulles: 'effervescent', rose: 'rose' };
      var alias = parGroupe[fam.groupe];
      score = alias ? (analyse.familles[alias] || 0) * 0.75 : 0;
    }
    if (!score) score = 0;

    /* ⚠️ `blanc_riche` N'EST PAS UNE FAMILLE DU CATALOGUE, c'est un STYLE.
       Trente-huit plats lui donnaient pourtant leur meilleure note — les
       Saint-Jacques, les crustacés, la truffe, le poisson en sauce, le
       foie gras. Comme aucune bouteille ne peut porter cette famille, ces
       notes tombaient dans le vide : tous ces plats retombaient sur
       `blanc_sec`, donc sur les deux ou trois mêmes blancs vifs de la
       cave. C'était 11 % du savoir d'accord perdu, et la cause première
       du manque de variété.
       On résout donc le style ici : un blanc AMPLE reçoit la note écrite
       pour le blanc riche, un blanc vif ne la reçoit qu'en partie. */
    if (fam.id === 'blanc_sec') {
      var vouluSec = analyse.familles.blanc_sec || 0;
      var vouluRiche = analyse.familles.blanc_riche || 0;
      if (vouluRiche) {
        score = produit.styles.blanc_riche
          ? Math.max(vouluSec, vouluRiche)
          : Math.max(vouluSec, vouluRiche * 0.7);
      }
    }

    /* --- Style : un rouge puissant sur un plat qui demande de la
           légèreté est un mauvais conseil, même si « rouge » est la
           bonne famille. --- */
    Object.keys(analyse.style || {}).forEach(function (k) {
      var voulu = analyse.style[k];
      var a = produit.styles[k] || 0;
      if (a) score += voulu * 14;
      else if (voulu >= 1 && (k === 'rouge_puissant' || k === 'rouge_leger')) {
        var oppose = k === 'rouge_puissant' ? 'rouge_leger' : 'rouge_puissant';
        if (produit.styles[oppose]) score -= voulu * 16;
      }
    });

    if (score <= 0) return null;

    var detail = { base: score, signaux: [] };

    /* =================================================================
       LES LOIS DE L'ACCORD (v6.0)
       -----------------------------------------------------------------
       Jusqu'ici, tout se jouait sur la FAMILLE : la bibliothèque disait
       « sur ce plat, du blanc sec à 94 », et n'importe quel blanc sec de
       la cave héritait de 94. Entre deux blancs secs, c'était l'ordre du
       catalogue qui tranchait — pas le sommelier.
       Ces lois comparent la STRUCTURE du plat à celle de la bouteille.
       Elles font deux choses qu'aucun tableau de familles ne sait faire :
         — départager deux vins de la même famille (le sauvignon tendu
           plutôt que le chardonnay boisé sur des huîtres) ;
         — répondre juste sur un plat que la bibliothèque ne connaît pas,
           puisque les axes d'un plat inconnu se lisent dans ses
           ingrédients.
       Un `veto` est une faute franche : aucun score ne la rattrape.
       ================================================================= */
    var axes = analyse.axes || {};
    var v = produit.profil;
    if (v) {
      var effetLois = 0;
      for (var li = 0; li < (S.lois || []).length; li++) {
        var loi = S.lois[li];
        if (!loi.si(axes)) continue;
        if (loi.veto && loi.veto(axes, v)) return null;
        var d = loi.effet(axes, v);
        if (!d) continue;
        effetLois += d;
        if (Math.abs(d) >= 8) {
          detail.signaux.push('loi:' + loi.id);
          if (d < 0 && !detail.faute) detail.faute = loi.explication;
          if (d > 0 && !detail.accord_structure) detail.accord_structure = loi.explication;
        }
      }
      /* Les lois AFFINENT le conseil, elles ne le remplacent pas : une
         bonne famille mal structurée doit reculer, pas disparaître, et
         une structure parfaite ne doit pas faire passer un vin devant la
         famille que le plat réclame. D'où ces bornes. */
      effetLois = clamp(effetLois, -55, 40);
      score += effetLois;
      detail.lois = Math.round(effetLois);
      if (score <= 0) return null;
    }

    /* --- Un dessert se boit dans un verre à vin, pas dans un verre à
           digestif. Un rhum ou un cognac sur un fondant sont des accords
           défendables, mais ils viennent APRÈS — au moment du café. Ils
           reculent donc franchement ici, et ne sont jamais « conseillés »
           à la place d'un vin doux. --- */
    /* ON NE SERT PAS D'EAU-DE-VIE À TABLE.
       Un armagnac est un accord juste sur le Sud-Ouest — mais APRÈS le
       repas, jamais à côté du magret. La règle valait déjà pour le
       dessert ; elle vaut tout autant pour l'entrée, le plat et le
       fromage. Restent libres l'apéritif (gin, whisky, pastis y sont
       chez eux) et le digestif, dont c'est le moment. */
    if (fam.groupe === 'spiritueux' &&
        (analyse.etape === 'dessert' || analyse.etape === 'entree' ||
         analyse.etape === 'plat' || analyse.etape === 'fromage')) {
      score -= 35;
      detail.signaux.push('hors_moment');
      if (score <= 0) return null;
    }

    /* --- Contre-indications : les fausses bonnes idées reculent, sans
           disparaître. Si la cave n'a rien d'autre, ou si le client y
           tient, le produit reste proposable — mais l'interface dira
           pourquoi il n'est pas idéal (règle n°23 : ne jamais présenter
           un accord comme une vérité). --- */
    var contre = analyse.deconseille && (analyse.deconseille[fam.id] || analyse.deconseille[fam.groupe]);
    if (contre) { score -= 40; detail.signaux.push('deconseille'); detail.contre = contre; }

    /* --- Le signal le plus fort : la cave a écrit elle-même que ce
           produit va avec ce plat, dans son champ « Accords mets ». --- */
    var trouveAccord = 0;
    (analyse.mots || []).forEach(function (m) {
      if (m.length >= 4 && contientMot(produit.texte_accords, m)) trouveAccord++;
    });
    /* Ce signal suit le POIDS de la lecture, et non sa simple présence.
       Sur « salade périgourdine », la fiche d'un rosé qui dit « salade »
       ne doit pas peser autant que sur une salade verte : le plat n'est
       une salade qu'à un quart. Sans cette pondération, un rosé de
       Provence passait devant un rouge du Sud-Ouest sur des gésiers
       confits — la fiche avait raison sur le mot, tort sur le plat. */
    (analyse.archetypes || []).forEach(function (a) {
      var part = a.part == null ? 1 : a.part;
      a.plat.mots.forEach(function (mot) {
        var n = norm(mot);
        if (n.length >= 5 && contientMot(produit.texte_accords, n)) trouveAccord += 1.5 * part;
      });
    });
    /* --- Le plat est d'une région, la bouteille aussi : c'est l'accord
           que le caviste propose en premier. Il ne renverse pas un
           mauvais accord — il départage deux bons. --- */
    if (analyse.terroir && analyse.terroir.length && produit.appellation_norm) {
      for (var tr = 0; tr < analyse.terroir.length; tr++) {
        if (contientMot(produit.appellation_norm, norm(analyse.terroir[tr]))) {
          score += 18; detail.signaux.push('terroir');
          break;
        }
      }
    }

    /* ⚠️ LES SIGNAUX DE LA MAISON SE CUMULENT ET PEUVENT TOUT RENVERSER.
       Fiche « Accords mets » (+45), recette trouvée (+55) et bouteille
       apprise (+45) s'additionnaient jusqu'à +145 — de quoi faire passer
       n'importe quelle bouteille devant n'importe quelle autre. C'est
       ainsi qu'un Chardonnay Vin de France à 5,90 € était conseillé sur
       une truffe : sa fiche parle de champignons, et ce seul mot pesait
       plus lourd que la famille, le rang et les lois réunis.
       Ces signaux doivent DÉPARTAGER des vins comparables, pas décider
       à la place du sommelier. On les additionne ici, on les plafonne
       plus bas. */
    var bonusMaison = 0;
    if (trouveAccord) {
      bonusMaison += Math.min(45, trouveAccord * 15);
      detail.signaux.push('accords_fiche');
    }

    /* --- Signal encore plus fort : une recette de la maison cite ce
           produit pour ce plat. --- */
    if (ctx.produitsRecettes && ctx.produitsRecettes[produit.handle]) {
      bonusMaison += 55 * ctx.produitsRecettes[produit.handle];
      detail.signaux.push('recette');
    }

    /* --- Bouteille citée par la maison pour ce plat, apprise de ses
           recettes. Le signal vaut presque celui d'une recette trouvée à
           l'écran — c'est la même main qui l'a écrit. --- */
    if (analyse.produits_appris && analyse.produits_appris[produit.handle]) {
      bonusMaison += 45 * analyse.produits_appris[produit.handle];
      detail.signaux.push('choix_maison');
    }

    /* LE PLAFOND. 60 points suffisent à faire gagner un vin sur un rival
       de valeur équivalente ; ils ne suffisent plus à renverser une
       famille mieux accordée ou un rang mieux adapté au plat. */
    score += Math.min(60, bonusMaison);
    detail.maison = Math.round(Math.min(60, bonusMaison));

    /* --- Étiquettes du produit --- */
    var trouveTag = 0;
    (analyse.mots || []).forEach(function (m) {
      if (m.length >= 5 && contientMot(produit.tags_norm, m)) trouveTag++;
    });
    if (trouveTag) { score += Math.min(14, trouveTag * 6); detail.signaux.push('etiquettes'); }

    /* --- Préférences déclarées par le client --- */
    if (ctx.preferences && ctx.preferences.length) {
      var voulue = ctx.preferences.indexOf(fam.groupe) > -1 || ctx.preferences.indexOf(fam.id) > -1;
      score += voulue ? 30 : -22;
    }
    if (ctx.bio && produit.bio) score += 18;
    if (ctx.local) {
      var local = S.filtres.filter(function (f) { return f.id === 'local'; })[0];
      if (local.mots.some(function (m) { return contientMot(produit.texte_recherche, norm(m)); })) score += 20;
    }

    /* --- Saison et moment : une orientation légère, jamais un
           renversement (règle n°22). --- */
    if (ctx.saison && ctx.saison.favorise.indexOf(fam.id) > -1) score += 5;
    if (ctx.moment && ctx.moment.favorise.indexOf(fam.id) > -1) score += 7;

    /* --- Mise en avant décidée depuis l'éditeur Shopify --- */
    if (ctx.avant && ctx.avant.indexOf(produit.handle) > -1) score += 25;

    /* --- Une fiche complète est un meilleur conseil qu'une fiche vide :
           on peut en dire quelque chose de vrai au client. --- */
    if (produit.temperature) score += 3;
    if (produit.accords) score += 3;
    if (produit.note || produit.caviste) score += 2;

    /* DÉPARTAGER LES ÉGAUX, SANS TIRER AU SORT.
       Sur des huîtres, huit blancs terminent au même score exact : le
       barème sature. L'ordre du catalogue tranchait alors, toujours dans
       le même sens — d'où la même bouteille conseillée sur des dizaines
       de recettes, et 37 références seulement sur 221 achetables.
       On ajoute donc moins de deux points, calculés à partir du COUPLE
       plat + bouteille : stable (le même plat propose toujours la même
       chose, rien ne bouge quand on retouche la page) mais différent d'un
       plat à l'autre. Trop petit pour renverser un vrai écart de
       qualité, assez grand pour que la cave tourne. */
    score += empreinte((analyse.texte || analyse.etape || '') + '|' + produit.handle) * 1.9;

    detail.total = score;
    /* =================================================================
       NIVEAU D'ACCORD ET CONFIANCE
       -----------------------------------------------------------------
       Un score brut ne veut rien dire tout seul : 120 est excellent sur
       un plat que la cave sert mal, et médiocre sur des huîtres. On le
       rapporte donc à ce que le PLAT réclame — la meilleure note que sa
       bibliothèque accorde à une famille de boisson. C'est l'accord
       idéal théorique ; le rapport dit à quelle distance on en est.

       Ce niveau sert à deux choses, et c'est la règle posée par
       Grégoire : mieux vaut deux excellentes bouteilles que trois dont
       une mauvaise. Il permet (1) de refuser d'occuper une case avec un
       accord tiède, (2) de dire au client ce que vaut vraiment le
       conseil qu'on lui donne.
       ================================================================= */
    /* ⚠️ NE PAS CALCULER LE NIVEAU SUR LE SCORE BRUT. Premier essai fait
       exactement cela : 95 % des bouteilles proposées ressortaient
       « exceptionnelles », ce qui ne renseigne personne. La cause est
       que le score inclut les signaux de la maison — une recette qui
       cite la bouteille vaut +55 — et dépasse donc largement la note que
       le plat accorde à la famille. On mesure ici la QUALITÉ DE
       L'ACCORD, pas le rang au classement :

         ajustement  ce que les lois de structure ont corrigé
         confirmation  la maison a écrit que ça marche (plafonné)
         réserve     contre-indication, spiritueux servi à table
    */
    /* ===============================================================
       LE VIN QUE LE SOMMELIER A DEMANDÉ
       ---------------------------------------------------------------
       Quand l'IA a décrit le vin qu'il faut — sa couleur, son tanin, sa
       fraîcheur, son corps —, on mesure l'écart entre cette demande et
       la structure réelle de la bouteille, que le moteur connaît déjà.
       C'est ce qui sépare enfin une fondue bourguignonne (rouge de soif,
       tanin 1, servi frais) d'un bœuf bourguignon (rouge de structure,
       tanin fondu) : deux plats de bœuf, deux rouges, mais plus jamais
       les mêmes bouteilles.
       Ce n'est pas un couperet : les interdits restent ceux de la
       bibliothèque et des lois. C'est un rapprochement, borné, qui
       départage à l'intérieur de ce qui est déjà permis.
       =============================================================== */
    if (analyse.vin_ideal) {
      var vise = ecartAuVinIdeal(produit, analyse.vin_ideal);
      if (vise) {
        if (vise.couleur === 0) score += 26;            /* la couleur voulue en premier */
        else if (vise.couleur > 0) score += 14;         /* une couleur acceptée ensuite */
        else score -= 30;                               /* pas une couleur demandée */
        /* Chaque point d'écart de structure coûte : un vin trop tannique
           ou trop mou pour ce plat recule, même de la bonne couleur. */
        score -= Math.min(34, vise.ecart * 5);
        detail.vin_ideal = { couleur: vise.couleur, ecart: Math.round(vise.ecart * 10) / 10 };
        detail.signaux.push('profil_sommelier');
        if (score <= 0) return null;
      }
    }

    /* ON NE SERT PAS UN VIN DE SOIF SUR UN PLAT DE FÊTE.
       Le moteur conseillait le Chardonnay le plus modeste de la cave sur
       une truffe, un plateau de fromages et une bouillabaisse : entre
       deux blancs amples il prenait le moins cher, faute de savoir ce que
       vaut une bouteille. Le rang de l'appellation le lui apprend.
       C'est un DÉPARTAGE, pas une exclusion : le vin modeste reste dans
       la rangée, à sa place de prix — il cesse simplement d'être celui
       qu'on met en avant. Et l'inverse vaut aussi : sur un plat familial,
       un cru perd son avantage, personne ne débouche un Pomerol sur un
       couscous. */
    var registrePlat = analyse.registre || 1;
    var rangVoulu = (S.rang_attendu || {})[registrePlat] || 1;
    var rangVin = produit.rang || 1;
    if (rangVin < rangVoulu) {
      score -= (rangVoulu - rangVin) * 19;
      detail.signaux.push('rang_insuffisant');
    } else if (rangVin > rangVoulu) {
      /* Trop de bouteille pour l'assiette. On ne l'écarte pas — un beau
         flacon reste proposable, c'est la case « Exception » de la
         rangée — mais il cesse d'être ce qu'on met en avant. Sans cela,
         un cru italien à 70 € portait le badge « Conseillé » sur un
         couscous royal. */
      score -= (rangVin - rangVoulu) * 10;
      detail.signaux.push('rang_excessif');
    }
    if (score <= 0) return null;

    var reference = 0;
    Object.keys(analyse.familles || {}).forEach(function (k) {
      if (analyse.familles[k] > reference) reference = analyse.familles[k];
    });
    if (reference < 40) reference = 40;

    var fit = clamp(detail.base / reference, 0, 1.12);
    var ajust = clamp((detail.lois || 0) / 150, -0.4, 0.27);
    var confirme = 0;
    if (detail.signaux.indexOf('recette') > -1) confirme += 0.08;
    if (detail.signaux.indexOf('choix_maison') > -1) confirme += 0.06;
    if (detail.signaux.indexOf('accords_fiche') > -1) confirme += 0.05;
    if (detail.signaux.indexOf('terroir') > -1) confirme += 0.04;
    confirme = Math.min(confirme, 0.15);
    var reserve = 0;
    if (detail.signaux.indexOf('deconseille') > -1) reserve += 0.30;
    if (detail.signaux.indexOf('hors_moment') > -1) reserve += 0.15;

    var accord = clamp(fit + ajust + confirme - reserve, 0, 1.2);
    detail.reference = Math.round(reference);
    detail.fit = Math.round(fit * 100) / 100;
    detail.accord = Math.round(accord * 100) / 100;

    var niveau = 'insuffisant', rang = 0;
    for (var nv = 0; nv < S.niveaux_accord.length; nv++) {
      if (accord >= S.niveaux_accord[nv].seuil) {
        niveau = S.niveaux_accord[nv].id; rang = S.niveaux_accord.length - nv; break;
      }
    }
    detail.niveau = niveau;

    return { produit: produit, score: score, detail: detail, niveau: niveau, rang: rang, accord: accord };
  }

  /* Empreinte stable d'une chaîne, entre 0 et 1. Aucun hasard : deux
     exécutions donnent le même nombre, ce qui est indispensable — une
     bouteille conseillée qui change à chaque clic serait insupportable. */
  function empreinte(s) {
    var h = 0;
    for (var i = 0; i < s.length; i++) { h = (h * 31 + s.charCodeAt(i)) | 0; }
    return Math.abs(h % 10007) / 10007;
  }

  /* =====================================================================
     7. CANDIDATS ET GAMMES
     ===================================================================== */

  /* Le produit appartient-il à l'un des rayons demandés ? Un rayon se
     décrit soit par groupes de familles (« vin » = rouge + rosé +
     blanc), soit par familles nommées (« rhum » = rhum + punch). */
  M.dansCategories = function (produit, ids) {
    var fam = produit.famille || {};
    for (var i = 0; i < ids.length; i++) {
      var cat = (S.categories || []).filter(function (c) { return c.id === ids[i]; })[0];
      if (!cat) continue;
      if ((cat.groupes || []).indexOf(fam.groupe) > -1) return true;
      if ((cat.familles || []).indexOf(fam.id) > -1) return true;
    }
    return false;
  };

  /* Ce que la cave peut réellement servir sous un filtre donné. Sert à
     n'afficher que des boutons qui mènent quelque part : proposer
     « Rosé » quand il n'en reste pas une bouteille achetable serait
     promettre pour rien. */
  M.couleursDisponibles = function (catalogue) {
    var vus = {};
    (catalogue || []).forEach(function (p) {
      if (p.dispo && p.couleur && p.famille.id !== 'autre') vus[p.couleur] = (vus[p.couleur] || 0) + 1;
    });
    return vus;
  };
  M.categoriesDisponibles = function (catalogue) {
    var vus = {};
    (S.categories || []).forEach(function (cat) {
      var n = 0;
      (catalogue || []).forEach(function (p) {
        if (p.dispo && p.famille.id !== 'autre' && M.dansCategories(p, [cat.id])) n++;
      });
      if (n) vus[cat.id] = n;
    });
    return vus;
  };

  M.candidats = function (analyse, catalogue, ctx) {
    var out = [];
    for (var i = 0; i < catalogue.length; i++) {
      var r = scorer(catalogue[i], analyse, ctx);
      if (r) out.push(r);
    }
    out.sort(function (a, b) { return b.score - a.score; });

    /* =================================================================
       LA PRIORITÉ EST AU VIN. Règle donnée par Grégoire, et c'est celle
       d'une cave : sur un plat, on conseille un vin, une bulle ou une
       bière. Un spiritueux ne passe JAMAIS devant, même quand son score
       est meilleur — sauf au DIGESTIF, qui est son moment à lui.
       Ce n'est pas un malus de score (un malus se rattrape), c'est un
       ordre de passage : les spiritueux sont renvoyés en fin de liste,
       leur classement entre eux étant conservé. Ils restent proposables
       — la cave vend du rhum et du whisky — mais après.
       ================================================================= */
    /* Une exception, et une seule : le client qui coche lui-même
       « Whisky » ou « Rhum » dans les filtres demande un spiritueux. On
       ne lui renvoie pas du vin. */
    var demandeSpiritueux = !!(ctx.categories && ctx.categories.length &&
      ctx.categories.some(function (id) {
        var c = (S.categories || []).filter(function (x) { return x.id === id; })[0];
        return c && ((c.groupes && c.groupes.indexOf('spiritueux') > -1) ||
                     (c.familles && c.familles.some(function (f) {
                       var fam = (S.familles || []).filter(function (y) { return y.id === f; })[0];
                       return fam && fam.groupe === 'spiritueux';
                     })));
      }));

    if (analyse.etape !== 'digestif' && !demandeSpiritueux) {
      var devant = [], derriere = [];
      for (var k = 0; k < out.length; k++) {
        if (out[k].produit.famille.groupe === 'spiritueux') {
          /* Marqué pour que la composition de la rangée le sache aussi :
             sans cette marque, l'éventail choisit « le meilleur score de
             la tranche de prix » et un cognac reprenait la tête. */
          out[k].recule = true;
          derriere.push(out[k]);
        } else devant.push(out[k]);
      }
      out = devant.concat(derriere);
    }
    return out;
  };

  /* L'ÉVENTAIL : quelques bouteilles étagées en prix pour un même plat.
     On découpe les candidats retenus en autant de tranches de prix que
     de propositions voulues, et dans CHAQUE tranche on garde le
     meilleur accord — jamais le moins cher pour le moins cher. Une cave
     qui s'enrichit fait donc bouger l'éventail toute seule, sans qu'on
     touche à un seuil de prix.
     Résultat trié du plus abordable au plus rare : c'est dans cet ordre
     que le client les lit. */
  /* Rang d'un niveau d'accord : 4 = exceptionnel … 0 = insuffisant. */
  function rangNiveau(id) {
    for (var i = 0; i < S.niveaux_accord.length; i++) {
      if (S.niveaux_accord[i].id === id) return S.niveaux_accord.length - i;
    }
    return 0;
  }
  M.rangNiveau = rangNiveau;

  M.eventail = function (candidats, combien, registre, cle) {
    var n = combien || 4;
    if (!candidats.length) return [];

    /* ================================================================
       LE PLANCHER DE QUALITÉ — la règle de Grégoire, appliquée ici.
       « Je préfère deux excellentes recommandations à trois dont une
       mauvaise. » La rangée est découpée en tranches de PRIX, et
       jusqu'ici chaque tranche devait rendre une bouteille : sur un plat
       mal servi par la cave, la case du bas se remplissait avec ce
       qu'on avait. Désormais une bouteille doit d'abord atteindre le
       niveau d'accord minimal ; à défaut, la case reste vide et
       l'interface le dit. Une case vide est une information honnête,
       une mauvaise bouteille est une faute.
       ================================================================ */
    var plancher = rangNiveau(S.plancher_rangee || 'bon');
    var dignes = candidats.filter(function (c) { return rangNiveau(c.niveau) >= plancher; });
    if (dignes.length) candidats = dignes;
    else return [];   /* aucun accord défendable : on ne propose rien */

    var meilleur = candidats[0].score;
    /* Plancher de qualité : on n'ira pas chercher un mauvais accord
       simplement pour remplir une case de prix. */
    var eligibles = candidats.filter(function (c) { return c.score >= meilleur * 0.55; });
    if (eligibles.length < n) eligibles = candidats.slice(0, Math.min(candidats.length, Math.max(n, eligibles.length)));

    var parPrix = eligibles.slice().sort(function (a, b) { return a.produit.prix_eur - b.produit.prix_eur; });

    /* PLANCHER DE REGISTRE — on n'ouvre pas la bouteille la moins chère
       de la cave sur une côte de bœuf ou un foie gras. On écarte le bas
       de la gamme avant de composer l'éventail, en gardant toujours de
       quoi remplir toutes les propositions. */
    var part = (S.planchers && S.planchers[registre || 1]) || 0;
    if (part > 0 && parPrix.length > n) {
      var saut = Math.min(Math.floor(parPrix.length * part), parPrix.length - n);
      if (saut > 0) {
        var coupes = parPrix.slice(0, saut);
        parPrix = parPrix.slice(saut);
        /* ⚠️ LE PLANCHER NE DOIT JAMAIS EMPORTER LE MEILLEUR ACCORD.
           Sur un foie gras (registre 3), il retirait de la rangée un
           moelleux à 10 € qui était la meilleure réponse de la cave, et
           que la maison lie elle-même à sa recette : écarté pour le seul
           motif d'être abordable. Aucun caviste ne fait ça. On le
           réintègre, à sa place dans l'ordre des prix. */
        var meilleurCoupe = null;
        coupes.forEach(function (c) { if (!meilleurCoupe || c.score > meilleurCoupe.score) meilleurCoupe = c; });
        var meilleurRestant = 0;
        parPrix.forEach(function (c) { if (c.score > meilleurRestant) meilleurRestant = c.score; });
        if (meilleurCoupe && meilleurCoupe.score > meilleurRestant) {
          parPrix.unshift(meilleurCoupe);
        }
      }
    }

    /* On écarte la queue de prix la plus extrême avant de découper.
       Sans cela, la dernière proposition d'un apéritif pouvait être une
       bouteille de collection à 298 € posée à côté d'une à 6,90 € : le
       client ne lit plus un éventail de budgets, il lit une aberration.
       La belle bouteille reste là — c'est le flacon de vitrine qui
       s'efface. Le garde-fou ne s'applique qu'avec assez de candidats
       pour que « la queue » veuille dire quelque chose. */
    /* PLAFOND — une rangée doit rester LISIBLE.
       Poser un flacon de collection à 295 € à côté d'un vin à 19 € ne
       donne pas le choix, cela donne le vertige : le client ne compare
       plus rien. On borne donc l'écart à quatre fois la bouteille la
       plus abordable de la rangée, ou six fois le prix médian — la plus
       généreuse des deux, pour qu'une belle bouteille reste possible. Ce
       n'est jamais un montant en dur : tout suit la cave.
       Le contrôle s'applique DÈS QU'IL Y A DE QUOI CHOISIR, et plus
       seulement sur les longues listes — c'est là qu'était le trou. */
    if (parPrix.length > n) {
      var bas = parPrix[0].produit.prix_eur;
      var median = parPrix[Math.floor(parPrix.length / 2)].produit.prix_eur;
      var plafond = Math.max(bas * 4, median * 6);
      var retenus = parPrix.filter(function (c) { return c.produit.prix_eur <= plafond; });
      if (retenus.length >= n) parPrix = retenus;
    }
    var total = parPrix.length;
    var res = [];
    var pris = {};

    for (var idx = 0; idx < n; idx++) {
      var debut = Math.floor(idx * total / n);
      var fin = idx === n - 1 ? total : Math.floor((idx + 1) * total / n);
      var tranche = parPrix.slice(debut, Math.max(fin, debut + 1));
      /* FAIRE TOURNER LA CAVE, SANS BAISSER LA QUALITÉ.
         Le barème ne descend pas au-dessous du point : sur une tranche
         de prix, plusieurs bouteilles sont réellement équivalentes, et
         prendre systématiquement la première laissait 37 références
         couvrir 336 recettes. On retient donc toutes celles qui sont à
         moins de 6 % du meilleur de la tranche — l'écart en deçà duquel
         un caviste dirait « l'une ou l'autre » — et on choisit dans ce
         groupe d'après une empreinte du plat. Résultat : stable pour un
         plat donné, mais différent d'un plat à l'autre. */
      var libres = tranche.filter(function (c) { return !pris[c.produit.handle]; });
      /* LA PRIORITÉ AU VIN VAUT AUSSI TRANCHE PAR TRANCHE. Sans cela, la
         règle posée dans `candidats` était contournée : la rangée prend
         « le meilleur score de chaque tranche de prix », et un cognac de
         65 € reprenait la case haute devant un vin doux. Un spiritueux
         n'occupe une case que si aucune bouteille de vin, de bulles ou
         de bière ne peut la tenir. */
      var sansSpiritueux = libres.filter(function (c) { return !c.recule; });
      if (sansSpiritueux.length) libres = sansSpiritueux;
      var choix = null;
      if (libres.length) {
        var haut = 0;
        libres.forEach(function (c) { if (c.score > haut) haut = c.score; });
        var egaux = libres.filter(function (c) { return c.score >= haut * 0.94; });
        choix = egaux[Math.floor(empreinte((cle || '') + '#' + idx) * egaux.length)] || egaux[0];
      }
      /* Tranche vide ou déjà servie : on prend le meilleur restant, et
         si le catalogue est trop court on s'arrête là plutôt que de
         répéter deux fois la même bouteille. */
      if (!choix) {
        for (var i = 0; i < parPrix.length; i++) {
          if (!pris[parPrix[i].produit.handle]) { choix = parPrix[i]; break; }
        }
      }
      if (!choix) break;
      pris[choix.produit.handle] = true;
      res.push(choix);
    }

    res.sort(function (a, b) { return a.produit.prix_eur - b.produit.prix_eur; });

    /* GARDE-FOU DE COHÉRENCE DE PRIX, sur la rangée FINALE.
       Le plafond calculé plus haut travaille sur la liste des candidats ;
       quand elle est courte, il ne s'applique pas et une bouteille de
       collection se retrouve à côté d'un vin de tous les jours — un
       Lafite à 750 € en face d'un Chardonnay à 5,90 €. Le client ne lit
       plus un éventail de budgets, il lit une provocation. On coupe donc
       ici, sur ce qui va réellement s'afficher : pas plus de huit fois
       la bouteille la plus abordable de la rangée. La rangée peut
       raccourcir — l'interface le dit —, mais elle reste lisible. */
    if (res.length > 1) {
      var basRangee = res[0].produit.prix_eur;
      if (basRangee > 0) {
        var coherents = res.filter(function (c) { return c.produit.prix_eur <= basRangee * 8; });
        if (coherents.length >= 2) res = coherents;
      }
    }

    /* ON NE LAISSE PAS DE CASE VIDE.
       Le garde-fou ci-dessus écartait la bouteille trop chère et
       s'arrêtait là : sur « huîtres », un champagne à 60 € sautait et le
       client ne voyait plus que TROIS bouteilles au lieu de quatre. Une
       case vide se lit comme une panne, pas comme une décision.
       On rappelle donc le meilleur accord restant qui tient dans
       l'écart de prix admis, jusqu'à compléter la rangée. */
    if (res.length < n) {
      var plafondRangee = res.length ? res[0].produit.prix_eur * 8 : Infinity;
      var dejaPris = {};
      res.forEach(function (c) { dejaPris[c.produit.handle] = true; });
      /* On repêche AU MEILLEUR ACCORD, pas au prix le plus bas : la case
         qui manque doit être remplie par un bon conseil, et les
         spiritueux restent derrière les vins comme partout ailleurs. */
      var repechage = parPrix.slice().sort(function (a, b) {
        if (!!a.recule !== !!b.recule) return a.recule ? 1 : -1;
        return b.score - a.score;
      });
      for (var rf = 0; rf < repechage.length && res.length < n; rf++) {
        var c2 = repechage[rf];
        if (dejaPris[c2.produit.handle]) continue;
        if (c2.produit.prix_eur > plafondRangee) continue;
        dejaPris[c2.produit.handle] = true;
        res.push(c2);
      }
      res.sort(function (a, b) { return a.produit.prix_eur - b.produit.prix_eur; });
    }

    return res;
  };

  /* CHAQUE COULEUR ADMISE DOIT ÊTRE REPRÉSENTÉE.
     Quand la maison a écrit qu'un plat se boit en rouge OU en rosé, la
     rangée doit montrer les deux : c'est le choix qu'un caviste pose
     sur la table, pas une seule couleur avec des variantes de prix.

     Sans cela, un signal fort mais partial suffisait à tout emporter —
     sur la périgourdine, les huit bouteilles liées à la recette maison
     (dont pas un seul rosé) raflaient les quatre places, et le client
     n'avait plus de choix de couleur du tout.

     On échange le moins bon accord d'une couleur déjà présente deux
     fois contre le meilleur accord de la couleur absente ; la bouteille
     évincée est donc toujours la plus faible de la rangée, jamais la
     conseillée. Le tri par prix est refait ensuite : l'éventail de
     budgets reste lisible. */
  M.diversifierCouleurs = function (choix, candidats, couleursAdmises) {
    if (!couleursAdmises || couleursAdmises.length < 2) return choix;
    if (choix.length < couleursAdmises.length) return choix;

    var res = choix.slice();
    couleursAdmises.forEach(function (couleur) {
      if (res.some(function (c) { return c.produit.couleur === couleur; })) return;

      var pris = {};
      res.forEach(function (c) { pris[c.produit.handle] = true; });
      var entrant = null;
      for (var i = 0; i < candidats.length; i++) {
        if (candidats[i].produit.couleur === couleur && !pris[candidats[i].produit.handle]) {
          entrant = candidats[i]; break;
        }
      }
      if (!entrant) return;   /* la cave n'a rien dans cette couleur : on n'invente pas */

      /* Victime : le plus faible accord parmi les couleurs en double —
         retirer une couleur pour en placer une autre ne réglerait rien. */
      var compte = {};
      res.forEach(function (c) { compte[c.produit.couleur] = (compte[c.produit.couleur] || 0) + 1; });
      var victime = null;
      res.forEach(function (c) {
        if (compte[c.produit.couleur] < 2) return;
        if (!victime || c.score < victime.score) victime = c;
      });
      if (!victime) return;

      res[res.indexOf(victime)] = entrant;
    });

    return res.sort(function (a, b) { return a.produit.prix_eur - b.produit.prix_eur; });
  };

  /* Les trois gammes nommées restent disponibles : c'est le même
     éventail, réduit à trois et étiqueté. */
  M.gammer = function (candidats) {
    var trois = M.eventail(candidats, 3, 1);
    var res = {};
    S.gammes.forEach(function (g, i) { if (trois[i]) res[g.id] = trois[i]; });
    return res;
  };

  /* Le meilleur produit capable de servir PLUSIEURS étapes à la fois.
     C'est ce qui évite d'ouvrir six bouteilles pour six plats (règle
     n°4) : on cherche l'intersection des candidats, pas la somme. */
  M.meilleurCommun = function (listesCandidats, tolerance) {
    var tol = tolerance == null ? 0.72 : tolerance;
    if (!listesCandidats.length) return null;
    var index = {}, meilleurs = [];

    listesCandidats.forEach(function (liste, i) {
      meilleurs[i] = liste.length ? liste[0].score : 0;
      liste.forEach(function (c) {
        var h = c.produit.handle;
        if (!index[h]) index[h] = { produit: c.produit, scores: [], total: 0 };
        index[h].scores[i] = c.score;
      });
    });

    var gagnant = null;
    Object.keys(index).forEach(function (h) {
      var e = index[h];
      var complet = true, total = 0, pire = 1;
      for (var i = 0; i < listesCandidats.length; i++) {
        var s = e.scores[i];
        if (s == null) { complet = false; break; }
        total += s;
        pire = Math.min(pire, meilleurs[i] ? s / meilleurs[i] : 0);
      }
      /* Un compromis n'est acceptable que s'il reste bon PARTOUT :
         c'est le maillon faible qui décide, pas la moyenne. */
      if (!complet || pire < tol) return;
      e.total = total; e.pire = pire;
      if (!gagnant || total > gagnant.total) gagnant = e;
    });
    return gagnant;
  };

  /* =====================================================================
     8. QUANTITÉS
     ---------------------------------------------------------------------
     Le calcul se fait en VERRES, pas en bouteilles. C'est la seule façon
     de traiter dans le même mouvement un vin (6 verres par bouteille) et
     un armagnac (17 verres) sans proposer de quantité absurde.

       verres par personne = volume de référence / 12,5 cl
       puis répartition sur les étapes réellement choisies
       puis, pour chaque boisson : convives × verres × contenance du verre
       puis conversion en bouteilles selon le format réel du produit.

     Une bouteille qui couvre deux étapes cumule leurs verres : on ne
     rajoute jamais mécaniquement une bouteille par plat.
     ===================================================================== */

  M.verresParEtape = function (ctx) {
    var niveau = S.niveaux_conso.filter(function (n) { return n.id === ctx.niveau; })[0] || S.niveaux_conso[1];
    var type = S.types_repas.filter(function (t) { return t.id === ctx.typeRepas; })[0] || S.types_repas[1];

    var clBase = niveau.cl;

    /* Un repas plus long fait boire davantage, mais pas
       proportionnellement : on borne des deux côtés. */
    var facteurDuree = clamp((ctx.duree || type.duree) / 120, 0.7, 1.6);
    var total = clBase * type.facteur * facteurDuree / 12.5;

    var etapes = ctx.etapes || [];
    var sommePoids = 0;
    etapes.forEach(function (id) {
      var e = S.etapes.filter(function (x) { return x.id === id; })[0];
      if (e) sommePoids += e.poids;
    });
    if (!sommePoids) return {};

    var out = {};
    etapes.forEach(function (id) {
      var e = S.etapes.filter(function (x) { return x.id === id; })[0];
      if (e) out[id] = total * (e.poids / sommePoids);
    });
    return out;
  };

  /* Convertit un besoin en volume vers un nombre de bouteilles, en
     proposant le format le plus juste. */
  M.bouteilles = function (produit, volumeCl, options) {
    var opt = options || {};
    var contenance = produit.contenance || 75;
    var exact = volumeCl / contenance;

    var minimum = Math.max(1, Math.round(exact));
    var confortable = Math.max(minimum, Math.ceil(exact));
    var marge = 0;
    if (opt.marge) marge = Math.max(0, Math.ceil(exact * (1 + S.marge_securite)) - confortable);

    var res = {
      minimum: minimum,
      confortable: confortable,
      marge: marge,
      exact: exact,
      contenance: contenance,
      format: produit.format,
      volume_total: confortable * contenance
    };

    /* Magnum : proposé seulement s'il existe vraiment en variante, et
       seulement à partir de deux bouteilles — sinon il n'apporte rien
       (règle n°4). */
    if (confortable >= S.seuil_magnum) {
      var mag = (produit.formats_variantes || []).filter(function (v) {
        return v.dispo && v.format && (v.format.id === 'magnum' || v.format.id === 'jeroboam');
      })[0];
      if (mag) {
        var nbMag = Math.max(1, Math.ceil(volumeCl / mag.format.cl));
        res.magnum = {
          variante: mag.id, titre: mag.titre, nombre: nbMag,
          prix_unitaire: mag.prix / 100,
          remplace: confortable,
          economie: (confortable * produit.prix_eur) - (nbMag * mag.prix / 100)
        };
      }
    }
    return res;
  };

  /* =====================================================================
     9. COHÉRENCE DU REPAS
     ---------------------------------------------------------------------
     Un bon accord plat par plat peut donner un mauvais repas. On
     contrôle donc l'ensemble : la progression, les répétitions, le
     nombre de références et le budget (règle n°5).
     ===================================================================== */

  M.coherence = function (plan, ctx) {
    var alertes = [];
    var lignes = plan.lignes.filter(function (l) { return l.choix && l.choix.produit; });

    /* --- Progression : on va du plus léger au plus puissant. Le
           dessert et le digestif changent volontairement de registre :
           ils sont hors contrôle. --- */
    var progression = lignes.filter(function (l) {
      return ['apero', 'entree', 'plat', 'fromage'].indexOf(l.etapes[0]) > -1;
    });
    for (var i = 1; i < progression.length; i++) {
      var av = progression[i - 1].choix.produit.puissance;
      var ap = progression[i].choix.produit.puissance;
      if (ap < av - 0.8) {
        alertes.push({
          type: 'progression', gravite: 'conseil',
          texte: 'Après ' + progression[i - 1].titre.toLowerCase() + ', ' +
                 progression[i].titre.toLowerCase() + ' revient à un vin plus léger : ' +
                 'le palais risque de le trouver fade. Servir les deux dans l’autre ordre, ou monter d’un cran.'
        });
      }
    }

    /* --- Contre-indications retenues malgré tout : on le dit. C'est
           exactement le rôle d'un caviste — prévenir plutôt que laisser
           découvrir à table. --- */
    lignes.forEach(function (l) {
      var d = l.analyse && l.analyse.deconseille;
      if (!d || !l.choix) return;
      var fam = l.choix.produit.famille;
      var raison = d[fam.id] || d[fam.groupe];
      if (raison) {
        alertes.push({
          type: 'contre_indication', gravite: 'conseil',
          texte: l.titre + ' — ' + raison
        });
      }
    });

    /* --- Répétitions : proposer six fois la même chose n'est pas une
           sélection, c'est un défaut de choix. --- */
    var familles = {};
    lignes.forEach(function (l) {
      var f = l.choix.produit.famille.id;
      familles[f] = (familles[f] || 0) + 1;
    });
    if (ctx.mode === 'precis') {
      Object.keys(familles).forEach(function (f) {
        if (familles[f] >= 4) {
          alertes.push({
            type: 'repetition', gravite: 'conseil',
            texte: 'Quatre étapes ou plus sur la même famille de vin : en mode « accords précis », ' +
                   'un contraste ferait davantage ressortir chaque plat.'
          });
        }
      });
    }

    /* --- Nombre de références --- */
    var refs = {};
    lignes.forEach(function (l) { refs[l.choix.produit.handle] = true; });
    var nbRefs = Object.keys(refs).length;
    if (ctx.mode === 'precis' && nbRefs >= 5) {
      alertes.push({
        type: 'references', gravite: 'info',
        texte: nbRefs + ' bouteilles différentes à ouvrir. Le mode « sélection simplifiée » ' +
               'regroupe les étapes compatibles si vous préférez en ouvrir moins.'
      });
    }

    /* --- Budget --- */
    if (ctx.budgetTotal && plan.total > ctx.budgetTotal) {
      alertes.push({
        type: 'budget', gravite: 'alerte',
        texte: 'Cette sélection dépasse votre budget de ' +
               Math.round(plan.total - ctx.budgetTotal) + ' €. ' +
               'La formule Essentielle, ou le remplacement d’une bouteille, y ramène.'
      });
    }

    return alertes;
  };

  /* =====================================================================
     10. CONSTRUCTION D'UNE FORMULE COMPLÈTE
     ---------------------------------------------------------------------
     Assemble tout : lecture des plats, choix des bouteilles, regroupement
     des étapes, quantités, cohérence et total.
     ===================================================================== */

  M.construire = function (opts) {
    var ctx = opts.contexte;
    var gamme = opts.gamme || 'signature';
    var plats = opts.plats;             /* [{etape, texte, analyse}] */
    var catalogue = opts.catalogue;

    var verres = M.verresParEtape(ctx);
    var lignes = [];

    /* --- Quelles étapes peuvent partager une bouteille --- */
    var groupes;
    if (ctx.mode === 'simplifie') {
      groupes = [['apero', 'entree'], ['plat', 'fromage'], ['dessert'], ['digestif']];
    } else {
      groupes = plats.map(function (p) { return [p.etape]; });
    }

    groupes.forEach(function (grp) {
      var membres = plats.filter(function (p) { return grp.indexOf(p.etape) > -1; });
      if (!membres.length) return;

      var listes = membres.map(function (p) {
        return M.candidats(p.analyse, catalogue, ctx);
      });

      var choix = null, partage = false;

      if (membres.length > 1) {
        /* On ne regroupe que si une bouteille reste un BON choix pour
           chaque étape du groupe. Sinon on préfère deux bouteilles à un
           mauvais compromis. */
        var commun = M.meilleurCommun(listes, 0.72);
        if (commun) {
          choix = { produit: commun.produit, score: commun.total / membres.length };
          partage = true;
        }
      }

      if (!choix) {
        /* Pas de compromis acceptable : chaque étape reprend sa
           liberté, même en mode simplifié. */
        membres.forEach(function (p, i) {
          var g = M.gammer(listes[i]);
          var c = g[gamme] || g.signature || g.essentiel;
          lignes.push(construireLigne([p], c, listes[i], verres, ctx, gamme));
        });
        return;
      }

      /* Un produit partagé doit lui aussi respecter la gamme demandée :
         on regamme sur l'intersection. */
      var intersection = listes[0].filter(function (c) {
        return listes.every(function (l) {
          return l.some(function (x) { return x.produit.handle === c.produit.handle; });
        });
      });
      var gInter = M.gammer(intersection.length ? intersection : listes[0]);
      var choixGamme = gInter[gamme] || choix;
      lignes.push(construireLigne(membres, choixGamme, intersection, verres, ctx, gamme, partage));
    });

    /* --- Parcours sans alcool, en parallèle et non en remplacement
           (règle n°9) --- */
    var softs = [];
    if (ctx.nbSansAlcool > 0 || ctx.parcours === 'sans_alcool') {
      var ctxSoft = Object.assign({}, ctx, { sansAlcool: true, preferences: [], couleurs: [], categories: [] });
      /* Le sans-alcool a son propre barème : on ne se boit pas un jus de
         fruits au rythme d'un verre de vin. On répartit le volume prévu
         par personne (réglable dans l'éditeur) au prorata des étapes,
         plutôt que de reprendre le compte de verres alcoolisés. */
      var sommeVerres = 0;
      Object.keys(verres).forEach(function (k) { sommeVerres += verres[k]; });
      var nbSoft = ctx.parcours === 'sans_alcool' ? ctx.nbAdultes : ctx.nbSansAlcool;

      plats.forEach(function (p) {
        var liste = M.candidats(p.analyse, catalogue, ctxSoft);
        if (!liste.length) return;
        var g = M.gammer(liste);
        var c = g[gamme] || g.essentiel;
        if (!c) return;
        var part = sommeVerres ? (verres[p.etape] || 0) / sommeVerres : 1;
        var volume = nbSoft * part * S.cl_sans_alcool_par_personne;
        softs.push({
          etapes: [p.etape], titre: p.texte, sans_alcool: true,
          choix: c, alternatives: liste.slice(0, 12),
          quantite: M.bouteilles(c.produit, volume, { marge: false }),
          volume_cl: volume,
          explication: M.expliquer(c.produit, [p.analyse])
        });
      });
    }

    var plan = { gamme: gamme, lignes: lignes, sans_alcool: softs, verres: verres };
    calculerTotaux(plan, ctx);
    plan.alertes = M.coherence(plan, ctx);
    return plan;

    function construireLigne(membres, choix, liste, verres, ctx, gamme, partage) {
      var etapes = membres.map(function (m) { return m.etape; });
      var titre = membres.map(function (m) { return m.texte; }).join(' · ');
      if (!choix) {
        return { etapes: etapes, titre: titre, choix: null, alternatives: [], vide: true };
      }
      var totalVerres = 0;
      etapes.forEach(function (e) { totalVerres += (verres[e] || 0); });
      var nbBuveurs = Math.max(0, ctx.nbAdultes - (ctx.nbSansAlcool || 0));
      if (ctx.parcours === 'sans_alcool') nbBuveurs = 0;
      var volume = nbBuveurs * totalVerres * (choix.produit.famille.verre || 12.5);

      return {
        etapes: etapes, titre: titre, partage: !!partage,
        choix: choix,
        alternatives: (liste || []).slice(0, 24),
        quantite: M.bouteilles(choix.produit, volume, { marge: ctx.niveau === 'genereuse' }),
        volume_cl: volume,
        /* Toutes les lectures du groupe : une bouteille qui couvre le
           plat ET les fromages doit être expliquée pour les deux. */
        explication: M.expliquer(choix.produit, membres.map(function (m) { return m.analyse; })),
        analyse: membres[0].analyse
      };
    }
  };

  function calculerTotaux(plan, ctx) {
    var total = 0, bouteilles = 0, volume = 0, refs = {};
    plan.lignes.concat(plan.sans_alcool).forEach(function (l) {
      if (!l.choix || !l.quantite) return;
      var n = l.quantite.confortable;
      total += n * l.choix.produit.prix_eur;
      bouteilles += n;
      volume += n * l.quantite.contenance;
      refs[l.choix.produit.handle] = true;
    });
    plan.total = Math.round(total * 100) / 100;
    plan.bouteilles = bouteilles;
    plan.volume_cl = volume;
    plan.references = Object.keys(refs).length;
    plan.par_personne = ctx.nbAdultes ? Math.round((total / ctx.nbAdultes) * 100) / 100 : 0;
    plan.cl_par_personne = ctx.nbAdultes ? Math.round(volume / ctx.nbAdultes) : 0;
  }

  /* =====================================================================
     11. EXPLICATION DE L'ACCORD
     ---------------------------------------------------------------------
     Concrète, propre au couple plat/vin, jamais un texte passe-partout
     (règle n°18). Trois sources, dans l'ordre : la raison écrite dans la
     bibliothèque pour ce plat et cette famille ; la note de dégustation
     de la fiche ; à défaut seulement, une phrase construite à partir des
     axes du plat.
     ===================================================================== */

  /* Les tournures portent déjà leur préposition contractée : c'est ce
     qui évite les « à le » quand la phrase est assemblée. */
  var PHRASES_AXES = {
    gras:   'au gras de l’assiette',
    iode:   'au caractère iodé',
    sucre:  'au sucré',
    epice:  'aux épices',
    tanin:  'à la chair de la viande',
    umami:  'aux saveurs longuement mijotées',
    acide:  'à l’acidité',
    fume:   'aux notes grillées',
    sel:    'au sel',
    amer:   'à l’amertume'
  };

  /* Familles suffisamment proches pour partager une explication. On ne
     va JAMAIS chercher au-delà : expliquer un moelleux avec la phrase
     d'un blanc sec (« sa tension, son acidité ») décrirait un vin que
     le client n'a pas dans son verre. */
  var VOISINES = {
    champagne: ['effervescent'], effervescent: ['champagne'],
    blanc_sec: ['blanc_riche'], blanc_riche: ['blanc_sec'],
    liquoreux: ['moelleux', 'vdn'], moelleux: ['liquoreux', 'vdn'], vdn: ['liquoreux', 'moelleux'],
    rouge: [], rose: []
  };

  M.expliquer = function (produit, analyses) {
    /* `analyses` accepte une lecture unique ou plusieurs, quand une même
       bouteille couvre deux étapes du repas. */
    var liste = Array.isArray(analyses) ? analyses : [analyses];
    var fam = produit.famille;
    var out = { court: '', long: '', source: '' };

    var raison = null;
    for (var i = 0; i < liste.length && !raison; i++) {
      var A = liste[i] || {};
      var R = A.raisons || {};
      raison = R[fam.id];
      if (!raison) {
        (VOISINES[fam.id] || []).some(function (v) {
          if (R[v]) { raison = R[v]; return true; }
          return false;
        });
      }
    }
    if (raison) { out.court = raison; out.source = 'bibliotheque'; }

    if (!out.court) {
      /* À défaut, on construit une phrase à partir de ce que le plat
         fait vraiment en bouche — en le nommant, pour que ce ne soit
         jamais une formule interchangeable (règle n°18). */
      var axes = {};
      liste.forEach(function (A) {
        Object.keys((A && A.axes) || {}).forEach(function (k) {
          axes[k] = Math.max(axes[k] || 0, A.axes[k]);
        });
      });
      var dominants = Object.keys(axes)
        .filter(function (k) { return axes[k] >= 1.3 && PHRASES_AXES[k]; })
        .sort(function (a, b) { return axes[b] - axes[a]; })
        .slice(0, 2);
      var plat = (liste[0] && liste[0].texte) ? liste[0].texte : '';
      if (dominants.length) {
        /* La famille est posée en tête, comme une étiquette : la phrase
           reste juste quel que soit le genre du mot (« la bière »,
           « le whisky »), sans bricolage d'articles. */
        out.court = fam.nom + (plat ? ' — sur « ' + plat + ' », ' : ' — ') +
          'l’accord répond ' + PHRASES_AXES[dominants[0]] +
          (dominants[1] ? ' et ' + PHRASES_AXES[dominants[1]] : '') + '.';
        out.source = 'axes';
      } else {
        out.court = fam.nom + (plat ? ' — un choix sûr sur « ' + plat + ' ».' : ' — une valeur sûre à ce moment du repas.');
        out.source = 'defaut';
      }
    }
    var certitude = liste[0] && liste[0].certitude;

    /* Le détail (« Pourquoi cet accord ? ») ajoute ce que la cave a
       écrit sur CETTE bouteille : c'est ce qui rend l'explication vraie
       plutôt que générique. */
    var bouts = [];
    if (produit.caviste) bouts.push('Le mot du caviste — ' + produit.caviste);
    else if (produit.note) bouts.push(produit.note);
    if (produit.accords) bouts.push('Accords de la fiche : ' + produit.accords);
    if (produit.temperature) bouts.push('Service : ' + produit.temperature);
    if (produit.aeration) bouts.push('Ouverture : ' + produit.aeration);
    out.long = bouts.join(' · ');

    if (certitude != null && certitude < 0.35) out.reserve = S.textes.accord_incertain;
    return out;
  };

  /* =====================================================================
     12. SAISON ET MOMENT
     ===================================================================== */

  M.saisonCourante = function (date) {
    var m = (date || new Date()).getMonth() + 1;
    return S.saisons.filter(function (s) { return s.mois.indexOf(m) > -1; })[0] || null;
  };

  M.momentDetecte = function (textes) {
    var t = norm((textes || []).join(' '));
    return S.moments.filter(function (mo) {
      return mo.mots.some(function (x) { return contientMot(t, norm(x)); });
    })[0] || null;
  };

  /* =====================================================================
     13. COCKTAILS
     ---------------------------------------------------------------------
     Un cocktail n'est utile que si l'on peut réellement acheter de quoi
     le faire. On rapproche donc chaque ingrédient du catalogue, et on
     tient compte des bouteilles DÉJÀ prévues pour le repas afin de ne
     pas les faire acheter deux fois (règle n°8).
     ===================================================================== */

  M.preparerCocktail = function (recette, catalogue, ctx, dejaPrevu) {
    var ingredients = [];
    try {
      var brut = recette.ingredients;
      if (typeof brut === 'string') brut = JSON.parse(brut);
      if (Array.isArray(brut)) ingredients = brut;
    } catch (e) { ingredients = []; }

    var nbVerres = Math.max(1, Math.round((ctx.nbAdultes || 2) * 1.5));
    var lignes = [];

    ingredients.forEach(function (ing) {
      var nom = norm(ing.ingredient || '');
      if (!nom) return;

      /* On passe D'ABORD par la famille de boisson nommée par
         l'ingrédient, et seulement ensuite par le catalogue. Chercher
         directement « vermouth rouge » dans les fiches ferait remonter
         un bordeaux rouge : une suggestion fausse est pire que pas de
         suggestion du tout. Le sucre, la menthe et le zeste d'orange ne
         sont pas du ressort d'un caviste : ils restent sans produit,
         et c'est très bien ainsi. */
      var familleId = null;
      for (var i = 0; i < S.ingredients_cocktail.length && !familleId; i++) {
        var e = S.ingredients_cocktail[i];
        for (var j = 0; j < e.mots.length; j++) {
          if (contientMot(nom, norm(e.mots[j]))) { familleId = e.famille; break; }
        }
      }

      var produit = null;
      if (familleId) {
        var dansFamille = catalogue.filter(function (p) {
          return p.dispo && p.famille.id === familleId;
        });
        /* Plafond de prix : on écarte les flacons de collection, qui ne
           se versent pas dans un shaker. Le repère est le prix médian de
           la famille, donc il s'ajuste tout seul à la cave. */
        var prixTries = dansFamille.map(function (p) { return p.prix_eur; }).sort(function (a, b) { return a - b; });
        var median = prixTries.length ? prixTries[Math.floor(prixTries.length / 2)] : 0;
        var plafond = median * (S.plafond_prix_cocktail || 2);
        var raisonnables = dansFamille.filter(function (p) { return !median || p.prix_eur <= plafond; });
        if (!raisonnables.length) raisonnables = dansFamille;

        /* À prix raisonnable, celle dont le nom partage le plus de mots
           avec l'ingrédient (« rhum blanc agricole »), et à égalité la
           plus abordable. */
        /* ⚠️ MIEUX VAUT NE RIEN PROPOSER QU'UNE BOUTEILLE À CÔTÉ.
           On prenait la meilleure bouteille de la famille MÊME quand aucun
           mot ne correspondait : « jus de citron vert » recevait le seul
           jus de la cave, un jus de RAISIN, et « rhum agricole » repartait
           avec un punch au rhum citron vert. Une suggestion fausse est
           pire que pas de suggestion — le client la met au panier.
           La règle est donc : la bouteille doit porter TOUS les mots
           distinctifs de l'ingrédient. « Rhum agricole » exige « rhum »
           ET « agricole » ; le punch est écarté, l'agricole sort. Sinon,
           l'ingrédient reste sans produit, et c'est très bien ainsi. */
        var GENERIQUES = ['jus', 'sirop', 'trait', 'traits', 'goutte', 'gouttes', 'dose', 'doses',
          'glacon', 'glacons', 'frais', 'fraiche', 'maison', 'bio', 'zeste', 'tranche', 'quartier'];
        var motsIng = mots(ing.ingredient).filter(function (m) {
          return m.length >= 4 && GENERIQUES.indexOf(m) < 0;
        });
        if (motsIng.length) {
          /* Un punch, un arrangé, une liqueur ou une crème ne répond JAMAIS
             à un ingrédient qui ne les nomme pas : ce sont des préparations
             sucrées, pas l'alcool de base du cocktail. */
          var demande = norm(ing.ingredient);
          var PREPARATIONS = ['punch', 'arrange', 'liqueur', 'creme de', 'sans alcool'];
          var exacts = raisonnables.filter(function (p) {
            var estPrep = PREPARATIONS.some(function (k) {
              return p.texte_bouteille.indexOf(k) > -1 && demande.indexOf(k) < 0;
            });
            if (estPrep) return false;
            return motsIng.every(function (m) { return contientMot(p.texte_bouteille, m); });
          });
          /* À égalité, la plus abordable : on ne pousse pas au flacon cher. */
          exacts.sort(function (a, b) { return a.prix_eur - b.prix_eur; });
          produit = exacts[0] || null;
        }
      }

      lignes.push({
        nom: ing.ingredient,
        quantite: ing.quantite || '',
        famille: familleId,
        produit: produit,
        deja_prevu: produit ? !!(dejaPrevu && dejaPrevu[produit.handle]) : false
      });
    });

    return { recette: recette, verres: nbVerres, ingredients: lignes };
  };

  /* =====================================================================
     14. MESURE
     ---------------------------------------------------------------------
     Aucune donnée personnelle n'est envoyée : ni texte libre saisi par
     le client, ni coordonnées, ni budget nominatif (règle n°20). On
     n'émet que des compteurs et des identifiants de produits, déjà
     publics.
     ===================================================================== */

  M.mesurer = function (evenement, donnees) {
    var charge = Object.assign({ outil: 'sommelier', version: S.version }, donnees || {});
    try {
      w.dataLayer = w.dataLayer || [];
      w.dataLayer.push(Object.assign({ event: 'cl_sommelier_' + evenement }, charge));
    } catch (e) {}
    try {
      document.dispatchEvent(new CustomEvent('cl:sommelier:' + evenement, { detail: charge }));
    } catch (e) {}
  };

  w.CLSommelierMoteur = M;
})(window);
